// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import React, { useEffect, useMemo, useRef, useState } from "react";
import { colorPalette } from "@directv-digital/dds.ui.dtv-theme";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import Skeleton from "@mui/material/Skeleton";
import DtvButton from "@directv-digital/dds.ui.dtv-button";
import Button from "@directv-digital/fusion.atoms.button";
import { LeftIcon, RightIcon } from "@directv-digital/fusion.atoms.icons";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvText from "@directv-digital/dds.ui.dtv-text";
import DtvGrid from "@directv-digital/dds.ui.dtv-grid";
import { EqualHeight, EqualHeightElement } from "react-equal-height/clean";
import {
  getAuthNToken,
  getCarousel,
  getmediaModal
} from "@directv-digital/dds.apis.dtv-ov-handlers";
import DtvMediaModal from "./dtv-media-modal";
import ShowCarousalParallax from "./show-carousal-parallax";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import { getMockResources } from "./mockResources";
import { fireCustomLinkClickGA, getAnalyticsAttrs } from "@directv-digital/dds.services.analytics-service";
import {
  reportShowCardKeyboardClick,
  SHOW_CAROUSEL_CARD_DESTINATION,
  SHOW_CAROUSEL_CARD_POSITION
} from "./show-carousal.analytics";
import DtvSwitch from "@directv-digital/dds.ui.dtv-switch";
import { safeHttpUrl } from "src/utils/UtilityFunctions";
import { resolveNetworkName } from "lib/helper/show-network";

type GenreConfig =
  | { type: 'genre'; genreType: string; showType: 'show' | 'movie' }
  | { type: 'keyword'; query: string; facetFilter?: string; filterTerms?: string[]; noFilter?: boolean }
  | { type: 'package'; packageFlag: string; fallbackFlag?: string; seedChannels: string[]; genresToSearch: string[] };

const _GENRE_API = 'https://api.cld.dtvce.com/disc/edge/genres/v1/service/genres';
const _KEYWORD_API = 'https://api.cld.dtvce.com/disc/edge/keywordsearch/v1/service/keyword';



async function _fetchByGenre(
  cfg: Extract<GenreConfig, { type: 'genre' }>,
  token: string,
  fisProperties: string,
  itemCount: number,
): Promise<any[]> {
  const url =
    `${_GENRE_API}/${encodeURIComponent(cfg.genreType)}/programs` +
    `?showType=${cfg.showType}` +
    `&fisProperties=${encodeURIComponent(fisProperties)}` +
    `&itemIndex=0&itemCount=${itemCount}&_tz=${Date.now()}`;
  try {
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray((data as any)?.resources) ? (data as any).resources : [];
  } catch { return []; }
}

async function _fetchByKeyword(
  cfg: Extract<GenreConfig, { type: 'keyword' }>,
  token: string,
  fisProperties: string,
  itemCount: number,
): Promise<any[]> {
  const BATCH = 50; const MAX = 300;
  const terms = cfg.filterTerms?.length ? cfg.filterTerms : [cfg.query.toLowerCase()];
  const accumulated: any[] = [];
  let cursor = 0; let upstream = 0;
  while (accumulated.length < itemCount && upstream < MAX) {
    const params = new URLSearchParams({
      query: cfg.query, itemCount: String(BATCH), itemIndex: String(cursor),
      facetFilter: cfg.facetFilter ?? 'Show', showFacets: 'false',
      includeExternalVOD: 'true', includeTVOD: 'true', sportsTitleConcat: 'false',
      clientContext: 'dmaID:807', fisProperties,
    });
    try {
      const res = await fetch(`${_KEYWORD_API}?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) break;
      const data = await res.json() as { resources?: any[]; hasMore?: boolean };
      const batch: any[] = Array.isArray(data?.resources) ? data.resources : [];
      upstream += batch.length;
      if (batch.length === 0) break;
      for (const r of batch) {
        if (accumulated.length >= itemCount) break;
        if (cfg.noFilter) { accumulated.push({ ...r, displayTitle: r.displayTitle ?? r.title }); continue; }
        const genres = (r?.genres || []).map((g: string) => (g || '').toLowerCase());
        const tl = (r?.title || r?.displayTitle || '').toLowerCase();
        const nl = (r?.networkAttributionName || '').toLowerCase();
        const pl = (r?.consumables?.[0]?.provider?.networkName || '').toLowerCase();
        if (terms.some(t => genres.some(g => g.includes(t)) || tl.includes(t) || nl.includes(t) || pl.includes(t))) {
          accumulated.push({ ...r, displayTitle: r.displayTitle ?? r.title });
        }
      }
      if (!data?.hasMore) break;
      cursor += BATCH;
    } catch { break; }
  }
  return accumulated;
}

async function _fetchByPackage(
  cfg: Extract<GenreConfig, { type: 'package' }>,
  token: string,
  fisProperties: string,
  itemCount: number,
): Promise<any[]> {
  const allowNames = new Set<string>(cfg.seedChannels.map(s => s.trim().toUpperCase()));
  const genreResults = await Promise.allSettled(
    cfg.genresToSearch.map(genre => _fetchByGenre({ type: 'genre', genreType: genre, showType: 'show' }, token, fisProperties, 100))
  );
  const seen = new Set<string>(); const pool: any[] = [];
  for (const result of genreResults) {
    if (result.status !== 'fulfilled') continue;
    for (const r of result.value) {
      if (!r.resourceId || seen.has(r.resourceId)) continue;
      seen.add(r.resourceId); pool.push(r);
    }
  }
  const filtered = pool
    .filter(r => {
      const names = new Set<string>();
      if (r?.networkAttributionName) names.add(r.networkAttributionName.toUpperCase());
      if (Array.isArray(r.consumables)) {
        for (const c of r.consumables) {
          if (c?.provider?.networkName) names.add(c.provider.networkName.toUpperCase());
          if (c?.channel?.networkName) names.add(c.channel.networkName.toUpperCase());
        }
      }
      for (const nm of names) if (allowNames.has(nm)) return true;
      return false;
    })
    .map(r => ({ ...r, displayTitle: r.displayTitle ?? r.title }));
  return filtered.slice(0, itemCount);
}

// Image spec for the modal hero. The tile itself uses the poster spec, which is the asset that
// carries the show's title treatment.
const HERO_FIS_SPEC = "iconic-bg,640,360,dc";
// Last-resort tile placeholder, used only when the CMS item count runs past the mock list.
const DEFAULT_TILE_BG_COLOR = colorPalette.jet.main;

// The modal is on unless CI explicitly turns it off. CMS delivers booleans as the strings
// "true"/"false" as well as real booleans, so both forms have to read the same.
export const isMediaModalEnabled = (value: any): boolean => value !== false && `${value}`.toLowerCase() !== "false";

// The fis image specs to request, in request order. OV returns `images[]` positionally — one entry
// per requested spec, no type discriminator — so a spec's index here is the index of its image on
// every resource, and every consumer must read the index this returns rather than a literal.
// `heroIndex` is -1 when the hero spec was not requested.
export const buildFisSpecs = (
  props: any
): { specs: string[]; heroIndex: number } => {
  const specs: string[] = [
    props?.orientation === "h" ?
      props?.horizontalProps || "poster-ni,320,180,dc" :
      "poster-ni,155,232,dc"
  ];
  let heroIndex = -1;
  // The refreshed variant reads the CMS value through the string-tolerant check. The classic variant
  // keeps the strict comparison it has always used: widening it there would add an image spec to
  // every non-parallax carousel whose CMS sends the string form, changing requests that work today.
  const wantsHero = props?.variant === "parallax" ?
    isMediaModalEnabled(props?.mediaModal) :
    props?.mediaModal === undefined || props?.mediaModal === true;
  if (wantsHero) {
    specs.push(HERO_FIS_SPEC);
    heroIndex = specs.length - 1;
  }
  return {
    specs,
    heroIndex
  };
};

// Primary network for the tile's second line. The precedence itself lives in lib/helper so the
// catalog's series-details route resolves the same value from the same fields — server/ cannot
// import this module. Re-exported because this is the import site consumers already use.
export { resolveNetworkName };

// Series enrichment for the refreshed tile (see fetchShowEnrichment): the number of series-details
// requests in flight at once, and the route that serves them. The /uf prefix is hardcoded, as it is
// at every other client call site of this route family — it is the app's asset prefix, and the
// server registers dev-only aliases so the same path resolves locally.
const SHOW_ENRICHMENT_CHUNK = 6;
const SHOW_ENRICHMENT_ENDPOINT = "/uf/api/catalog/series-details";
// Per-request ceiling. The waves are sequential, so without it one hung upstream head-of-line
// blocks every later wave and the rest of the carousel never gets its network line at all. Long
// enough that a slow-but-working upstream still lands (the route fronts it with its own cache, so
// only the first visitor pays the full round trip), short enough that the later waves still arrive
// while the visitor is plausibly still on the same carousel.
const SHOW_ENRICHMENT_TIMEOUT_MS = 8000;

// A Set that is created once and lives for the component's lifetime. `useRef(new Set())` would
// allocate and immediately discard one on every render, and keeping the lazy check here leaves the
// caller a single declaration.
const useIdSet = (): Set<string> => {
  const ref = useRef<Set<string>>(null);
  if (!ref.current) {
    ref.current = new Set<string>();
  }
  return ref.current;
};

// AbortSignal.timeout is well past this app's compile target (it needs Chrome/Edge 103, Firefox
// 100, Safari 15.4) and the app ships no polyfill for it, so calling it unguarded throws before the
// request is even sent — costing every episode-derived tile its network line. AbortController is
// far older, so it carries the timeout where the former is missing. There is deliberately no third
// tier for a browser missing AbortController too: the only wave that reaches here runs through
// Promise.allSettled, whose own floor is higher, so such a browser throws out of the wave before a
// single result can be applied — an unsignalled request there would buy nothing. lib/helper/timeout
// is exactly the second tier below and nothing more, so it is not reused: it has no native-timeout
// path of its own.
const enrichmentTimeoutSignal = (ms: number): AbortSignal => {
  if (typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function") {
    return AbortSignal.timeout(ms);
  }
  const controller = new AbortController();
  setTimeout(() => controller.abort(), ms);
  return controller.signal;
};

// Series-level network name and artwork for one series id, or null when the route declined. The
// route answers with a minimal `{network, image}` projection, so nothing here needs reshaping.
// A normally transient fault throws so the caller can log it; a dropped series resolves null and
// stays silent, because no one can act on it.
const fetchSeriesDetails = async (seriesId: string) => {
  const response = await fetch(
    `${SHOW_ENRICHMENT_ENDPOINT}/${encodeURIComponent(seriesId)}`,
    { signal: enrichmentTimeoutSignal(SHOW_ENRICHMENT_TIMEOUT_MS) }
  );
  if (!response.ok) {
    // The same split the route itself makes, and what decides whether the caller warns: 4xx means
    // the catalog has dropped a series the feed still lists, which is steady state, while 5xx
    // (including the route's own 503 when it cannot get an upstream token) and 429 are faults.
    if (response.status >= 500 || response.status === 429) {
      throw new Error(`series-details responded ${response.status}`);
    }
    return null;
  }
  return response.json();
};

export const XS_SMALL_SCREEN = "@media (min-width: 0px) and (max-width: 600px)";
export const SMALL_SCREEN_VERTICAL =
  "@media (min-width: 429px) and (max-width: 539px)";
export const SMALL_TABLET_SCREEN_VERTICAL =
  "@media (min-width: 600px) and (max-width: 1099px)";
export const SMALL_SCREEN = "@media (min-width: 0px) and (max-width: 600px)";
export const TABLET_SCREEN =
  "@media (min-width: 600px) and (max-width: 1099px)";
export const SMALL_DESKTOP_SCREEN =
  "@media (min-width: 1099px) and (max-width: 1699px)";
export const SMALL_LAPTOP_DESKTOP_SCREEN = "@media (min-width: 1099px)";

type orientationType = "h" | "v";

export enum SchemaTypes {
  MOVIE_CAROUSEL = "https://schema.org/ItemList",
  MOVIE = "https://schema.org/Movie",
  LIST_ITEM = "https://schema.org/ListItem",
}

export enum SchemaProps {
  POSITION = "position",
  NAME = "name",
  IMAGE = "image",
  DESCRIPTION = "description",
  URL = "url",
  ITEM_LIST_ELEMENT = "itemListElement",
  ITEM = "item",
  DIRECTOR = "director",
  ACTOR = "actor",
  DATE_PUBLISHED = "datePublished",
  GENRE = "genre",
  CONTENT_RATING = "contentRating",
}

export type ShowCarousalProps = {
  /* Non-CI authorable fields*/
  lazyload?: boolean;
  isLoading?: boolean;
  /** */
  /* To update component id */
  id?: string;
  /* To add carousal images and content from OV api. Not CI authorable field. */
  cards: any;
  /* To add header Image(s) above carousal(s) */
  headerImages?: any;
  /* To add heading(s) above the banner(s), uses DtvText modal */
  headers?: any;
  /* To add subHeading(s) directly above the banner(s), uses DtvText modal */
  subHeadings?: any;
  /** To add button(s) below carousel */
  ctas?: any;
  /* To enable a second carousel to display */
  isDblCarousel?: boolean;
  /* To add background color to the component */
  backgroundColor?: string;
  /* To add the font color to content */
  color?: string;
  /* To add the required number of carousal items to show. In horizontal orientation default are set to 5 and in vertical orientation of carousal image it is set to 8 */
  itemCount?: any;
  /* TO add product type - stream or satellite. Acceptable values are stream and satellite. @default satellite */
  productType?: string;
  /* To add 'pageReference' from OV api to get the speicific caoursal items s*/
  referenceId?: any;
  /* TO add orientation to carousal images. Acceptable values are h (horizontal) and v(vertical).*/
  orientation?: orientationType;
  /* To Hide and show Description text. Acceptable values are yes and no @default no */
  showDescription?: any;
  /* To Hide and show Title text from OV API below each card. Acceptable values are yes and no @default no */
  showFromTitleKey?: any;
  /* To Hide and show Channel info like schedule and channel Number. Acceptable values are true and false @default false */
  enableChannelInfo?: boolean;
  /* To display either cropped image or full image inside the modal. Acceptable values are true and false @default false */
  cropImage?: boolean;
  /* To add channel keyword for channel Number  */
  channelLabel?: string;
  /* To Hide and show Title. Acceptable values are yes and no @default no */
  showTitle?: any;
  mediaModal?: boolean;
  mediaModalCta1?: any;
  mediaModalCta2?: any;
  /* Variant selector. "parallax" renders the multi-row parallax layout; unset keeps the classic layout */
  variant?: string;
  /* Number of rows for the refresh variant. 1 = single row (no parallax), 2 = two rows scrolling in opposite directions @default 2. CMS sends this as a string ("1"/"2"); the parallax component coerces. */
  rows?: number | string;
  /* Per-show modal CTA overrides keyed by resourceId. Falls back to mediaModalCta1/mediaModalCta2 when absent */
  ctaOverrides?: Record<string, { cta1?: any; cta2?: any }>;
  /* To Hide the left right arrows */
  hideArrows?: boolean;
  /* To add the custom styles to Carousal */
  carousalStyles?: any;
  /* To add the custom styles to cardMedia */
  cardmediaStyles?: any;
  /* To hide the default mock content on API Failure */
  showMockContentOnApiFailure?: boolean;
  /* To disable the card click event which stops to move different page or open modal popup */
  disableCardClick?: boolean;
  /* To open the media content on new tab instead of same page */
  openCardClickInNewTab?: boolean;
  /* To add the custom styles to media contains gridStyles, cardStyles, cardmediaStyles  */
  mediaStyles?: any;
  horizontalProps?: string;
  configType?: 'genre' | 'keyword' | 'package';
  genreType?: string;
  keywordQuery?: string;
  keywordFacetFilter?: string;
  keywordFilterTerms?: string;
  packageFlag?: string;
  packageFallbackFlag?: string;
  seedChannels?: string;
  genresToSearch?: string;
  /* Configuration for live score display */
  liveScoreConfig?: {
    showScoreLabel?: string;
    apiUrl?: string;
    refreshInterval?: number;
  };
  /* Data to display on each channel */
  channelData?: any,
  pageKey?: string;
};

interface Image {
  imageUrl: string;
  defaultImageUrl: string;
  dominantColor?: any;
  defaultDominantColor?: any;
  width?: any;
  height?: any;
}

interface Card {
  title?: string;
  description?: string;
  image: Image;
  resourceId?: any;
  mockId?: any;
  mockBackground?: any;
  resourceType?: any;
}

interface Resource {
  resourceId: string;
  resourceType: string;
  title?: string;
  description?: string;
  image?: Image;
  bgColor: string;
}

// const LeftIcon = (props) => (
//   // <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
//     <path
//       style={{ fill: props?.mode === "dark" ? "#FFFFFF" : "#102641" }}
//       d="M22.5247 5.51972C23.1473 5.51972 23.7802 5.75643 24.26 6.21017C25.2093 7.13738 25.2093 8.6367 24.26 9.55404L15.5019 17.9975L24.2804 26.4608C25.2399 27.388 25.2399 28.8774 24.2804 29.8046C23.3209 30.7318 21.7795 30.7318 20.82 29.8046L11.0003 20.3353C10.3675 19.7237 10 18.8754 10 17.9877C10 17.1098 10.3573 16.2516 10.9901 15.6401L20.7894 6.19045C21.2692 5.72685 21.8918 5.5 22.5145 5.5" fill="#121212" />
//   // </svg>
// );

// const RightIcon = (props) => (
//   // <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
//     <path
//       style={{ fill: props?.mode === "dark" ? "#FFFFFF" : "#102641" }}
//       d="M13.4753 30.4803C12.8526 30.4803 12.2198 30.2435 11.7401 29.7898C10.7908 28.8626 10.7908 27.3633 11.7401 26.446L20.4981 18.0025L11.7196 9.53925C10.7601 8.61204 10.7601 7.12261 11.7196 6.1954C12.6791 5.2682 14.2205 5.2682 15.18 6.1954L24.9997 15.6647C25.6325 16.2763 26 17.1246 26 18.0123C26 18.8902 25.6428 19.7484 25.0099 20.3599L15.2107 29.8095C14.7309 30.2731 14.1082 30.5 13.4855 30.5" fill="#121212" />
//   // </svg>
// );

const styles = {
  scoreDisplayStyles: {
    color: "#fff",
    width: "48px",
    textAlign: "center",
    position: "absolute",
    bottom: {
      xs: "22%",
      sm: "26%",
      md: "23%",
      lg: "26%",
      xl: "24%"
    }
  }
};

const InProgressIcon = () => (
    <svg
      fill="none"
      height="5"
      viewBox="0 0 5 5"
      width="5"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="2.5" cy="2.5" fill="#6BBFA1" r="2.5" />
    </svg>
);

// eslint-disable-next-line complexity
export function CarouselComponent(props: ShowCarousalProps) {
  const {
    id,
    orientation,
    showDescription,
    showTitle,
    showFromTitleKey,
    isLiveScoreVariant,
    channelData
  } = props;
  const backgroundColor = props?.backgroundColor?.toUpperCase() || colorPalette.neutral_white.main;

  const [showScores, setShowScores] = useState(false);
  const [mediaModalData, setMediaModalData] = useState({});
  const [cardImgHeight, setCardImgHeight] = useState(0);
  const estCardDescHeight =
    showDescription && showDescription?.toLowerCase() === "yes" ?
      !(showTitle && showTitle?.toLowerCase() === "no") ?
        26 : /* both*/
        9.5 : /** description only */
      !(showTitle && showTitle?.toLowerCase() === "no") ?
        !(showFromTitleKey && showFromTitleKey?.toLowerCase() === "no") ?
          29 : /** title & titleKey */
          14.5 : /** title only */
        0; // neither
  // Generated by Copilot- Start - 2026-03-31 18:31:32
  /**
   * getLayoutWidth — SSR-safe viewport width helper
   * Returns the layout viewport width (px), matching how CSS @media queries
   * evaluate min-width / max-width (i.e. EXCLUDING the scrollbar).

   *   window.innerWidth includes the scrollbar width (~15-17px on Windows Chrome).
   *   CSS @media queries use clientWidth, so using innerWidth causes the JS breakpoint
   *   to fire earlier than the CSS breakpoint by ~15px (e.g. 1083px instead of 1099px).

   * Breakpoint value (1099):
   *   CSS: @media (min-width: 1099px) → desktop
   *   Matches old behavior where arrows appear at 1099px and above.
   */
  const getLayoutWidth = (): number => {
    if (typeof document !== "undefined" && document.documentElement) {
      return document.documentElement.clientWidth;
    }
    if (typeof window !== "undefined") {
      return window.innerWidth;
    }
    return 0;
  };

  const [isDesktopUp, setIsDesktopUp] = useState<boolean>(
    () => getLayoutWidth() >= 1099
  );
  // Generated by Copilot- End - 2026-03-31 18:31:32
  const [href, setHref] = useState<string | undefined>(undefined);
  useEffect(() => {
    setHref(window.location.href);
  }, []);
  const tileSpacing = orientation === "v" ? "5px" : "7px";
  const mockResources = getMockResources(backgroundColor);

  function handleResize() {
    /**
     * assumping both carousels card heights are same height due to EqualHeightElement,
     * we check only the first carousel card height
     */
    const img =
      (document?.querySelector(`[id=carousel_tile_${id}_0]`) as
        | HTMLImageElement
        | HTMLDivElement) || null;
    if (img && img?.offsetHeight) {
      setCardImgHeight(img?.offsetHeight);
    } else {
      setCardImgHeight(0);
    }
    setIsDesktopUp(getLayoutWidth() >= 1099);
  }

  useEffect(() => {
    ["resize", "load"].forEach(ev => window.addEventListener(ev, handleResize));
    handleResize();
    return () => {
      ["resize", "load"].forEach(ev => window.removeEventListener(ev, handleResize));
    };
  }, []);

  useEffect(() => {
    handleResize();
    handleScroll(0);
    if (props?.isDblCarousel) {
      handleScroll(1);
    }
  }, [props?.isLoading]);

  useEffect(() => {
    handleScroll(0);
    if (props?.isDblCarousel) {
      handleScroll(1);
    }
  }, [isDesktopUp]);

  // eslint-disable-next-line camelcase
  const fn_isScrolledLeft = (cNum: number): boolean => {
    const carousel =
      (document?.querySelector(
        `[id=${id}-card-container-${cNum}]`
      ) as HTMLDivElement) || null;
    if (carousel) {
      return carousel.scrollLeft === 0;
    }
    return false;
  };

  // eslint-disable-next-line camelcase
  const fn_isScrolledRight = (cNum: number): boolean => {
    const carousel =
      (document?.querySelector(
        `[id=${id}-card-container-${cNum}]`
      ) as HTMLDivElement) || null;
    if (carousel) {
      return (
        Math.ceil(carousel.scrollLeft) >=
        carousel.scrollWidth - carousel.clientWidth - 5
      );
    }
    return false;
  };

  const handleScroll = (cNum: number) => {
    const carousel =
      (document?.querySelector(
        `[id=${id}-card-container-${cNum}]`
      ) as HTMLDivElement) || null;
    if (carousel) {
      const arrowLeft =
        (document?.querySelector(
          `[id=${id}-chevron-left-${cNum}]`
        ) as HTMLButtonElement) || null;
      const arrowRight =
        (document?.querySelector(
          `[id=${id}-chevron-right-${cNum}]`
        ) as HTMLButtonElement) || null;
      const isScrolledLeft = fn_isScrolledLeft(cNum);
      const isScrolledRight = fn_isScrolledRight(cNum);
      if (arrowLeft && arrowRight) {
        if (!isDesktopUp) {
          arrowLeft.style.opacity = "0";
          arrowRight.style.opacity = "0";
        } else {
          arrowLeft.style.opacity = isScrolledLeft ? "0.2" : "1";
          arrowRight.style.opacity = isScrolledRight ? "0.2" : "1";
        }
        arrowLeft?.setAttribute(
          "aria-disabled",
          isScrolledLeft ? "true" : "false"
        );
        arrowRight?.setAttribute(
          "aria-disabled",
          isScrolledRight ? "true" : "false"
        );
        arrowLeft.style.pointerEvents = isScrolledLeft ? "none" : "";
        arrowRight.style.pointerEvents = isScrolledRight ? "none" : "";
      }
    }
  };

  const handleButtonClick = (cNum: number, arrowClicked: string): void => {
    const carousel =
      (document?.querySelector(
        `[id=${id}-card-container-${cNum}]`
      ) as HTMLDivElement) || null;
    if (carousel) {
      const newScroll =
        carousel.scrollLeft +
        carousel.offsetWidth * (arrowClicked === "left" ? -1 : 1);
      carousel.scrollTo({
        left: newScroll,
        behavior: "smooth"
      });
    }
  };

  // A card opens the media modal unless the CMS explicitly opts out, so an absent flag
  // still counts. This is the CMS intent only — whether a given card actually reacts to a
  // click is the `interactive` value each render path computes.
  const opensMediaModal =
    typeof props?.mediaModal === "undefined" || props?.mediaModal === true;

  // The heading doubles as the card's accessible name, and getAnalyticsAttrs drops the whole
  // attribute set when it is missing, so fall through the remaining title fields.
  const getCardLinkName = card => {
    const name =
      card?.mediaModalData?.heading || card?.title || card?.alt || "";
    return name?.toLowerCase?.();
  };

  const handleInjectAnalytics = (card, interactive = true) => {
    // A card that does not react to a click must not advertise a destination — it would be
    // reported as a modal open that never happens.
    if (!interactive) {
      return {};
    }
    if (opensMediaModal) {
      // No id: the wrapper this is spread onto already carries its own
      // card-resource-container id, and injecting it again duplicates that id onto the
      // inner card.
      return getAnalyticsAttrs(getCardLinkName(card), SHOW_CAROUSEL_CARD_POSITION, SHOW_CAROUSEL_CARD_DESTINATION);
    }
    const commonInfoUrl = `/guide/${
      card.resourceType?.toLowerCase() === "series" ?
        "tv-shows" :
        card.resourceType?.toLowerCase() === "network" ?
        "networks" :
        "movies"
    }/${card?.title?.replace?.(/[^a-zA-Z0-9 ]/g, "")?.replace?.(/\s+/g, "-")}-${card.resourceId}`;
    const origin = typeof window === "undefined" ? "" : window.location.origin;
    return getAnalyticsAttrs(card.title?.title?.toLowerCase(), "panel_seasonal favorites & more", `${origin}${commonInfoUrl}`);
  };

  const reportCardKeyboardClick = card => {
    // The keydown handler stays attached when card clicks are disabled, but handleCardClick
    // returns early, so there is nothing to report.
    if (!opensMediaModal || props?.disableCardClick) {
      return;
    }
    reportShowCardKeyboardClick(getCardLinkName(card));
  };

  const dateToFormat = (dateToFormat: any) => {
    const formatedDate = new Date(dateToFormat);
    const dateString = formatedDate
      .toLocaleString("en-US", {
        year: "2-digit",
        day: "numeric",
        month: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZone: "America/New_York",
        hour12: true
      })
      .replace(",", "  ")
      .replace("AM", "AM EST")
      .replace("PM", "PM EST");

    return dateString;
  };

  const closeMediaModal = () => {
    setMediaModalData({});
  };

  const handleCardClick = async card => {
    if (card?.resourceId && !props?.disableCardClick) {
      if (opensMediaModal) {
        let linkname: any = card?.mediaModalData?.heading?.toLowerCase();
        if (props?.enableChannelInfo) {
          const contentType =
            card?.resourceType.toLowerCase() === "series" ?
              "tv-shows" :
              card.resourceType.toLowerCase() === "network" ?
                "networks" :
                "movies";
          const browserId =
            props?.productType === "stream" ?
              "DFW_WEB_Chrome_G" :
              "DTVE_DFW_WEB_Chrome_G";

          const accessTokenResponse = await getAuthNToken(browserId);
          await new Promise((resolve, _reject) => {
            const res: any = getmediaModal(
              contentType,
              card.resourceId,
              accessTokenResponse.access_token
            );
            resolve(res);
          }).then((data: any) => {
            const channelNumber: string =
              data?.consumables[0]?.channel?.majorChannelNumber || "";
            const modalTitle: any = data?.displayTitle ?
              data?.displayTitle?.toLowerCase() :
              linkname;
            linkname = modalTitle;

            setMediaModalData({
              ...card.mediaModalData,
              heading: modalTitle,
              body: `${dateToFormat(data?.consumables?.[0]?.startTime)}` || "",
              subheading: channelNumber ?
                `${props?.channelLabel} ${channelNumber}` :
                "",
              modalOpen: true
            });
          });
        } else {
          setMediaModalData({
            ...card.mediaModalData,
            modalOpen: true
          });
        }
      } else {
        const commonInfoUrl = `/guide/${
          card.resourceType?.toLowerCase() === "series" ?
            "tv-shows" :
            card.resourceType?.toLowerCase() === "network" ?
            "networks" :
            "movies"
        }/${card?.title
          ?.replace?.(/[^a-zA-Z0-9 ]/g, "")
          ?.replace?.(/\s+/g, "-")}-${card.resourceId}`;

        if (props?.openCardClickInNewTab) {
          window.open(`${window.location.origin}${commonInfoUrl}`, "_blank");
        } else {
          window.location.href = commonInfoUrl;
        }
      }
    }
  };

  const titleStyles = {
    titleWrap: {
      whiteSpace: "normal",
      lineHeight: "normal",
      overflow: "auto",
      fontSize: "14px !important"
    },
    titleNoWrap: {
      whiteSpace: "nowrap",
      lineHeight: "normal",
      textOverflow: "ellipsis",
      overflow: "hidden"
    }
  };

  const renderMockCard = (type: string, ix: number): JSX.Element => {
    try {
      return type === "double" ? (
        <DtvGrid
          container
          className="card"
          key={ix}
          spacing={1}
          tabIndex={0}
          sx={{
            scrollSnapAlign: "start",
            minWidth: {
              xs:
                orientation === "v" ?
                  "28.57%" : // 3.5 cards
                  "40%", // 2.5 cards
              md:
                orientation === "v" ?
                  "25%" : // 4 cards
                  "33.33%" // 3 cards
            }
          }}
        >
          <DtvBox
            sx={{
              mx: {
 xs: "5px",
md: tileSpacing
},
              borderRadius: 0,
              boxShadow: "none",
              overflow: "initial",
              background: "transparent"
            }}
          >
            <Skeleton
              component="div"
              id={`carousel_tile_${id}_${ix}`}
              variant="rectangular"
              sx={{
                height: "fit-content",
                width: "100%",
                aspectRatio: orientation !== "v" ? "1/0.56" : "1/1.4935",
                backgroundColor: mockResources[ix]?.bgColor
              }}
            />
          </DtvBox>
        </DtvGrid>
      ) : (
        <DtvGrid
          item
          key={ix}
          tabIndex={0}
          sx={{
            scrollSnapAlign: "start",
            ...(!props?.mediaStyles?.cardStyles &&
              (orientation === "v" ?
                {
                    "@media (min-width: 0px )": {
                      maxWidth: "40%",
                      minWidth: "40%"
                    },
                    "@media (min-width: 429px )": {
                      maxWidth: "33%",
                      minWidth: "33%"
                    },
                    "@media (min-width: 539px)": {
                      maxWidth: "25%",
                      minWidth: "25%"
                    },
                    "@media (min-width: 749px)": {
                      maxWidth: "18.18%", // tablet 5 1/2
                      minWidth: "18.18%"
                    },
                    "@media screen and (min-width: 999px)": {
                      maxWidth: "14.28%",
                      minWidth: "14.28%"
                    },
                    "@media screen and (min-width: 1699px)": {
                      maxWidth: "12.5%",
                      minWidth: "12.5%"
                    }
                  } :
                {
                    "@media (min-width: 0px )": {
                      maxWidth: "66%",
                      minWidth: "66%"
                    },
                    "@media (min-width: 429px )": {
                      maxWidth: "60%",
                      minWidth: "60%"
                    },
                    "@media (min-width: 749px)": {
                      maxWidth: "40%",
                      minWidth: "40%"
                    },
                    "@media screen and (min-width: 1099px)": {
                      maxWidth: "25%",
                      minWidth: "25%"
                    },
                    "@media screen and (min-width: 1699px)": {
                      maxWidth: "20%",
                      minWidth: "20%"
                    }
                  }))
          }}
        >
          <DtvBox
            sx={{
              mx: {
 xs: "5px",
md: tileSpacing
},
              borderRadius: 0,
              boxShadow: "none",
              overflow: "initial",
              background: "transparent"
            }}
          >
            <Skeleton
              component="div"
              id={`carousel_tile_${id}_${ix}`}
              variant="rectangular"
              sx={{
                height: "fit-content",
                width: "100%",
                aspectRatio: orientation !== "v" ? "1/0.56" : "1/1.4935",
                backgroundColor: mockResources[ix]?.bgColor,
                ...(props?.mediaStyles?.cardmediaStyles ||
                  props?.cardmediaStyles)
              }}
            />
          </DtvBox>
        </DtvGrid>
      );
    } catch (_error) {
      return <></>;
    }
  };

  let singleCarouselObj: any;
  let doubleCarouselObj: any;
  if (!props?.isDblCarousel) {
    singleCarouselObj = {
      content: {
        header:
          (props?.headers?.[0] ? props?.headers?.[0] : props?.headers) || null,
        headerImage:
          (props?.headerImages?.[0] ?
            props?.headerImages?.[0] :
            props?.headerImages) || null,
        subheading:
          (props?.subHeadings?.[0] ?
            props?.subHeadings?.[0] :
            props?.subHeadings) || null,
        showScoreLabel:
          (props?.liveScoreConfig?.[0] ?
            props?.liveScoreConfig?.[0]?.showScoreLabel :
            props?.liveScoreConfig?.showScoreLabel) || "Show scores"
      },
      mockCards:
        orientation === "v" ? // generate max # cards that can show
          [1, 2, 3, 4, 5, 6, 7, 8].map(ix => renderMockCard("single", ix)) :
          [1, 2, 3, 4, 5].map(ix => renderMockCard("single", ix))
    };
  } else {
    doubleCarouselObj = {
      content: [0, 1].map(cNum => ({
        header:
            (Array.isArray(props?.headers?.[cNum]) &&
            props?.headers?.[cNum]?.length > 0 ?
              props?.headers?.[cNum][0] :
              props?.headers?.[cNum]) || null,
        headerImage:
            (Array.isArray(props?.headerImages?.[cNum]) &&
            props?.headerImages?.[cNum]?.length > 0 ?
              props?.headerImages?.[cNum][0] :
              props?.headerImages?.[cNum]) || null,
        subheading:
            (Array.isArray(props?.subHeadings?.[cNum]) &&
            props?.subHeadings?.[cNum]?.length > 0 ?
              props?.subHeadings?.[cNum][0] :
              props?.subHeadings?.[cNum]) || null
      })),
      mockCards:
        orientation === "v" ? // generate max # cards that can show
          [1, 2, 3, 4].map(ix => renderMockCard("double", ix)) :
          [1, 2, 3].map(ix => renderMockCard("double", ix))
    };
  }

  const handleToggleScores = () => {
    setShowScores(prev => {
      const newShow = !prev;
      fireCustomLinkClickGA({
        event: "trackEvent",
        event_name: "custom_link_click",
        linkName: `${newShow ? "show" : "hide"} scores`,
        linkPosition: singleCarouselObj?.content?.header?.content || "",
        linkDestination: window.location.href
      });
      return newShow;
    });
  };

  const isToday = someDate => {
    const today = new Date();
    return (
      someDate.getDate() === today.getDate() &&
      someDate.getMonth() === today.getMonth() &&
      someDate.getFullYear() === today.getFullYear()
    );
  };

  const isTomorrow = someDate => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1); // Add one day to get tomorrow's date
    return (
      someDate.getDate() === tomorrow.getDate() &&
      someDate.getMonth() === tomorrow.getMonth() &&
      someDate.getFullYear() === tomorrow.getFullYear()
    );
  };

  try {
    return (
      <>
        <DtvMediaModal {...mediaModalData} closeMediaModal={closeMediaModal} />
        {id && (props?.isDblCarousel || props?.cards?.length > 1) ? (
          <EqualHeight animationSpeed={0} key={"showCarousel"}>
            <DtvBox
              id={id}
              {...props.getSchemOrgData(true, SchemaTypes.MOVIE_CAROUSEL, "")}
              sx={{
                padding: {
                  xs: "32px 20px",
                  lg: " 68px 20px 53px 20px"
                },
                background: backgroundColor,
                margin: "0 !important",
                borderRadius: "10px",
                display: "flex wrap",
                justifyContent: "center",
                transition: "padding 200ms linear"
              }}
            >
              <DtvBox
                sx={{
                  display: {
                    xs: "block",
                    sm: "flex"
                  },
                  justifyContent: "center",
                  alignItems: "center"
                }}
              >
                {/* eslint-disable-next-line complexity */}
                {[0, 1].map(cNum => (
                    <DtvBox
                      key={cNum}
                      sx={{
                        maxWidth: {
                          xs: "100%",
                          sm: "50%"
                        },
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        flexGrow: 1,
                        minHeight:
                          props?.isDblCarousel && orientation === "h" ?
                            "0px" :
                            "200px"
                      }}
                    >
                      {doubleCarouselObj.content[cNum].headerImage?.url && (
                        <DtvBox
                          sx={{
                            display: "flex",
                            width: "100%",
                            height: "140px",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: {
                              xs: cNum > 0 ? "16px 0 10px 0" : "0 0 10px 0",
                              sm: "0 0 10px 0"
                            },
                            scrollSnapAlign: "start"
                          }}
                        >
                          <img
                            alt={
                              doubleCarouselObj.content[cNum].headerImage
                                ?.alt ||
                              (doubleCarouselObj.content[cNum].header?.content ?
                                doubleCarouselObj.content[cNum].header
                                    ?.content :
                                "Carousel header image")
                            }
                            src={
                              doubleCarouselObj.content[cNum].headerImage?.url
                            }
                            style={{
                              maxHeight: "100%",
                              maxWidth: "100%",
                              objectFit: "contain"
                            }}
                          ></img>
                        </DtvBox>
                      )}
                      {doubleCarouselObj.content[cNum].header?.content && (
                        <EqualHeightElement name="doubleCarouselObj.content[cNum].header">
                          <DtvBox
                            sx={{
                              width: "100%",
                              justifyContent: "right",
                              px: {
                                xs: "8px",
                                sm: "24px",
                                md: "32px"
                              },
                              height: "100%",
                              display: "flex",
                              alignItems: "center"
                            }}
                          >
                            <DtvText
                              color={
                                doubleCarouselObj.content[1].header?.color !==
                                doubleCarouselObj.content[0].header?.color ?
                                  doubleCarouselObj.content[0].header?.color :
                                  doubleCarouselObj.content[cNum].header
                                      ?.color
                              }
                              component={
                                doubleCarouselObj.content[cNum].header
                                  ?.component || "h3"
                              }
                              content={
                                doubleCarouselObj.content[cNum].header?.content
                              }
                              cta={{
                                ...(doubleCarouselObj.content[cNum].header?.cta
                                  ?.length > 0 &&
                                  doubleCarouselObj.content[
                                    cNum
                                  ].header?.cta?.label?.trim() !== "" && {
                                    label:
                                      doubleCarouselObj.content[cNum].header
                                        ?.cta?.label || "",
                                    legalUrl:
                                      doubleCarouselObj.content[cNum].header
                                        ?.cta?.legalUrl,
                                    position:
                                      doubleCarouselObj.content[cNum].header
                                        ?.cta?.position
                                  })
                              }}
                              sx={{
                                color:
                                  doubleCarouselObj.content[cNum].header?.color,
                                paddingBottom: "20px",
                                textAlign: "center",
                                width: "100%",
                                fontSize: {
                                  xs: "28px",
                                  sm: "28px",
                                  md: "28px",
                                  lg: "36px",
                                  xl: "36px"
                                }
                              }}
                              tooltip={{
                                ...(doubleCarouselObj.content[cNum].header
                                  ?.tooltip?.length > 0 &&
                                  doubleCarouselObj.content[
                                    cNum
                                  ].header?.tooltip?.content?.trim() !== "" && {
                                    content:
                                      doubleCarouselObj.content[cNum].header
                                        ?.tooltip?.content,
                                    position:
                                      doubleCarouselObj.content[cNum].header
                                        ?.tooltip?.position,
                                    mode: doubleCarouselObj.content[cNum].header
                                      ?.tooltip?.mode,
                                    sx: {
                                      color:
                                        doubleCarouselObj.content[cNum].header
                                          ?.color
                                    }
                                  })
                              }}
                              variant={
                                doubleCarouselObj.content[cNum].header
                                  ?.variant || "body1"
                              }
                            />
                          </DtvBox>
                        </EqualHeightElement>
                      )}
                      {doubleCarouselObj.content[cNum].subheading?.content && (
                        <DtvBox
                          sx={{
                            display: "flex",
                            width: "100%",
                            justifyContent: "right",
                            padding: "inherit 16 inherit 16",
                            px: {
                              xs: "8px", //
                              md: "32px"
                            }
                          }}
                        >
                          <DtvText
                            color={
                              doubleCarouselObj.content[1].subheading?.color !==
                              doubleCarouselObj.content[0].subheading?.color ?
                                doubleCarouselObj.content[0].subheading?.color :
                                doubleCarouselObj.content[cNum].subheading
                                    ?.color
                            }
                            component={
                              doubleCarouselObj.content[cNum].subheading
                                ?.component || "h3"
                            }
                            content={
                              doubleCarouselObj.content[cNum].subheading
                                ?.content
                            }
                            cta={{
                              ...(doubleCarouselObj.content[cNum].subheading
                                ?.cta && {
                                label:
                                  doubleCarouselObj.content[cNum].subheading
                                    ?.cta?.label || "",
                                legalUrl:
                                  doubleCarouselObj.content[cNum].subheading
                                    ?.cta?.legalUrl,
                                position:
                                  doubleCarouselObj.content[cNum].subheading
                                    ?.cta?.position
                              })
                            }}
                            sx={{
                              color:
                                doubleCarouselObj.content[cNum].subheading
                                  ?.color,
                              marginBottom: "24px",
                              textAlign: "left",
                              width: "100%",
                              ml: "inherit"
                            }}
                            tooltip={{
                              ...(doubleCarouselObj.content[cNum].subheading
                                ?.tooltip && {
                                content:
                                  doubleCarouselObj.content[cNum].subheading
                                    ?.tooltip?.content,
                                position:
                                  doubleCarouselObj.content[cNum].subheading
                                    ?.tooltip?.position,
                                mode: doubleCarouselObj.content[cNum].subheading
                                  ?.tooltip?.mode,
                                sx: {
                                  color:
                                    doubleCarouselObj.content[cNum].subheading
                                      ?.color
                                }
                              })
                            }}
                            variant={
                              doubleCarouselObj.content[cNum].subheading
                                ?.variant || "subhead2"
                            }
                          />
                        </DtvBox>
                      )}

                      <DtvBox
                        sx={{
                          width: {
                            xs: "100%",
                            md: "calc(100% - 98px)",
                            lg: "calc(100% - 108px)"
                          },
                          transition: "width 300ms ease",
                          flexGrow: 1,
                          justifyContent: "center",
                          position: "relative", // context for absolute position buttons
                          paddingRight: isDesktopUp ?
                            "0" :
                            cNum === 0 ?
                            "16px" :
                            "0", // padding for 1st carousel
                          paddingLeft: isDesktopUp ?
                            "0" :
                            cNum !== 0 ?
                            "16px" :
                            "0", // padding for 2nd carousel
                          paddingBottom: "3px"
                        }}
                      >
                        <DtvBox
                          sx={{
                            display: "flex",
                            width: "100%",
                            overflow: "hidden"
                          }}
                        >
                          {!props?.hideArrows && (
                            <Button
                              aria-disabled={true}
                              aria-label="Click to scroll the carousel left"
                              buttontype="tertiary"
                              icon-only="true"
                              iconOnly={true}
                              id={`${props?.id}-chevron-left-${cNum}`}
                              size="medium"
                              googleanalyticsdata={getAnalyticsAttrs(
                                `carousel-${cNum + 1}-left-arrow`,
                                "panel_seasonal favorites & more",
                                href
                              )}
                              icon={
                                // @ts-expect-error DDS Icon component has stricter prop typing than actual usage
                                <LeftIcon
                                  mode={
                                    backgroundColor.includes("FEFEFE") ||
                                    backgroundColor.includes("FFF") ?
                                      "light" :
                                      "dark"
                                  }
                                />
                              }
                              mode={
                                backgroundColor.includes("FEFEFE") ||
                                backgroundColor.includes("FFF") ?
                                  "light" :
                                  "dark"
                              }
                              sx={{
                                // sizing
                                minWidth: "unset !important",
                                width: "42px !important",
                                height: "42px !important",
                                padding: "0px !important",
                                margin: "0 !important",
                                visibility: isDesktopUp ? "visible" : "hidden",
                                // position
                                position: "absolute",
                                top:
                                  cardImgHeight !== 0 ?
                                    `${cardImgHeight / 2}px` :
                                    !props?.isLoading ?
                                    `calc(50% - ${estCardDescHeight / 2}px)` :
                                    "50%",
                                transform: "translateY(-50%)", // center origin
                                // scrolling
                                transition:
                                  "opacity 300ms ease, top 100ms linear",
                                left: "-44px",
                                opacity: "0.4"
                              }}
                              onClick={() => handleButtonClick(cNum, "left")}
                            ></Button>
                          )}
                          <DtvGrid
                            container
                            id={`${id}-card-container-${cNum}`}
                            sx={{
                              display: "flex",
                              flexWrap: "nowrap",
                              position: "relative",
                              justifyContent: props?.isLoading ?
                                "flex-start" :
                                "normal",
                              maxWidth: "100%",
                              overflowX: "auto",
                              scrollSnapType: "x proximity",
                              scrollbarWidth: "none",
                              scrollBehavior: "smooth",
                              pb: "10px",
                              "&::-webkit-scrollbar": {
                                width: "0px",
                                background: "transparent",
                                display: "none"
                              },
                              "&::-webkit-scrollbar-thumb": {
                                width: "0px",
                                background: "transparent",
                                display: "none"
                              }
                            }}
                            onScroll={() => handleScroll(cNum)}
                          >
                            {props?.isLoading && doubleCarouselObj?.mockCards}
                            {!props?.isLoading &&
                              props?.cards?.[cNum]?.resources &&
                              props.cards[cNum].resources?.map(
                                (card: Resource, ix) => {
                                  const interactive =
                                    !!card?.resourceId && !props?.disableCardClick;
                                  return (
                                    <DtvGrid
                                      id={`card-resource-container-${cNum}-${ix}`}
                                      key={ix}
                                      role={interactive ? "button" : undefined}
                                      tabIndex={interactive ? 0 : -1}
                                      {...(interactive && {
                                        onKeyDown: e => {
                                          // A held Enter repeats the keydown; one activation is one event.
                                          if (e.key === "Enter" && !e.repeat) {
                                            reportCardKeyboardClick(card);
                                            handleCardClick(card);
                                          }
                                        }
                                      })}
                                      {...handleInjectAnalytics(card, interactive)}
                                      item
                                      {...props.getSchemOrgData(
                                        true,
                                        SchemaTypes.LIST_ITEM,
                                        SchemaProps.ITEM_LIST_ELEMENT
                                      )}
                                      sx={{
                                        scrollSnapAlign: "start",
                                        minWidth: {
                                          xs:
                                            orientation === "v" ?
                                              "28.57%" : // 3.5 cards
                                              "40%", // 2.5 cards
                                          md:
                                            orientation === "v" ?
                                              "25%" : // 4 cards
                                              "33.33%" // 3 cards
                                        },
                                        maxWidth: {
                                          xs:
                                            orientation === "v" ?
                                              "28.57%" :
                                              "40%",
                                          md:
                                            orientation === "v" ?
                                              "25%" :
                                              "33.33%"
                                        }
                                      }}
                                    >
                                      <meta
                                        content={String(ix + 1)}
                                        itemProp={SchemaProps.POSITION}
                                      />
                                      {card?.resourceId ? (
                                        <Card
                                          className={"card"}
                                          key={ix}
                                          onClick={() => handleCardClick(card)}
                                          /* Unlike the single-row layout below, this layout's
                                             onClick is not gated on the live score variant, so
                                             `interactive` is not either. */
                                          {...handleInjectAnalytics(card, interactive)}
                                          {...props.getSchemOrgData(
                                            true,
                                            SchemaTypes.MOVIE,
                                            SchemaProps.ITEM
                                          )}
                                          sx={{
                                            mx: {
 xs: "5px",
md: tileSpacing
},
                                            cursor: card.resourceId ?
                                              isLiveScoreVariant ? "default" : "pointer" :
                                              "auto",
                                            borderRadius: 0,
                                            boxShadow: "none",
                                            overflow: "initial",
                                            background: "transparent"
                                          }}
                                        >
                                          <CardMedia
                                            className={`carousel_tile_${  id}`}
                                            component={"img"}
                                            id={`carousel_tile_${id}_${ix}`}
                                            itemProp={SchemaProps.IMAGE}
                                            loading={"lazy"}
                                            alt={
                                              card?.title ?
                                                card.title :
                                                "carousel card"
                                            }
                                            src={
                                              card?.image?.imageUrl ?
                                                card.image?.imageUrl :
                                                "https://www.directv.com/dtvassets/global/component-assets/guide/EmptySVG.svg"
                                            }
                                            sx={{
                                              // height: card?.image?.height ? card?.image?.height : orientation === 'v' ? "232px" : "156px",
                                              width: card && card?.image?.width,
                                              aspectRatio:
                                                orientation !== "v" ?
                                                  "1/0.56" :
                                                  "1/1.4935",
                                              backgroundColor: card?.bgColor,
                                              "&:hover": {
                                                boxShadow: isLiveScoreVariant ? "" : "0px 0px 5px #666"
                                              },
                                              cursor: isLiveScoreVariant ? "default" : "pointer"
                                            }}
                                            onError={e => {
                                              const target =
                                                e.target as HTMLImageElement;
                                              target.onerror = null;
                                              target.src =
                                                card.image?.defaultImageUrl ||
                                                "";
                                            }}
                                          />
                                          {card?.title && (
                                            <CardContent
                                              itemProp={SchemaProps.NAME}
                                              sx={{
                                                padding: "0 !important",
                                                mt: "8px"
                                              }}
                                            >
                                              <DtvBox
                                                component="div"
                                                sx={{
                                                  typography: "body2",
                                                  whiteSpace: "nowrap",
                                                  overflow: "hidden",
                                                  textOverflow: "ellipsis",
                                                  color: props?.color
                                                }}
                                              >
                                                {card?.title}
                                              </DtvBox>
                                            </CardContent>
                                          )}
                                          {card?.description && (
                                            <CardContent
                                              itemProp={SchemaProps.DESCRIPTION}
                                              sx={{ padding: "0 !important" }}
                                            >
                                              <DtvText
                                                aria-label={card?.description}
                                                component="div"
                                                content={card?.description}
                                                variant="smalltext"
                                                sx={{
                                                  whiteSpace: "nowrap",
                                                  lineHeight: "normal",
                                                  pb: "3px",
                                                  textOverflow: "ellipsis",
                                                  color: props?.color,
                                                  cursor: isLiveScoreVariant ? "default" : "pointer"
                                                }}
                                              />
                                            </CardContent>
                                          )}
                                          <meta
                                            itemProp={SchemaProps.URL}
                                            content={`/guide/movies/${card?.title
                                              ?.replace?.(/[^a-zA-Z0-9 ]/g, "")
                                              ?.replace?.(/\s+/g, "-")}-${
                                              card.resourceId
                                            }`}
                                          />
                                          {card.mediaModalData?.subheading &&
                                            card.mediaModalData.subheading.includes(
                                              "|"
                                            ) && (
                                              <meta
                                                itemProp={SchemaProps.GENRE}
                                                content={card.mediaModalData.subheading
                                                  .split("|")
                                                  .pop()
                                                  .trim()}
                                              />
                                            )}
                                        </Card>
                                      ) : (
                                        renderMockCard("double", ix)
                                      )}
                                    </DtvGrid>
                                  );
                                }
                              )}
                          </DtvGrid>
                          {!props?.hideArrows && (
                            <Button
                              aria-disabled={false}
                              aria-label="Click to scroll the carousel right"
                              buttontype="tertiary"
                              icon-only="true"
                              iconOnly={true}
                              id={`${props?.id}-chevron-right-${cNum}`}
                              size="medium"
                              googleanalyticsdata={getAnalyticsAttrs(
                                `carousel-${cNum + 1}-right-arrow`,
                                "panel_seasonal favorites & more",
                                href
                              )}
                              icon={
                                // @ts-expect-error DDS Icon component has stricter prop typing than actual usage
                                <RightIcon
                                  mode={
                                    props?.mode === "dark" ? "dark" : "light"
                                  }
                                />
                              }
                              mode={
                                backgroundColor.includes("FEFEFE") ||
                                backgroundColor.includes("FFF") ?
                                  "light" :
                                  "dark"
                              }
                              sx={{
                                // sizing
                                minWidth: "unset !important",
                                width: "42px !important",
                                height: "42px !important",
                                padding: "0px !important",
                                margin: "0 !important",
                                visibility: isDesktopUp ? "visible" : "hidden",
                                // position
                                position: "absolute",
                                top:
                                  cardImgHeight !== 0 ?
                                    `${cardImgHeight / 2}px` :
                                    !props?.isLoading ?
                                    `calc(50% - ${estCardDescHeight / 2}px)` :
                                    "50%",
                                transform: "translateY(-50%)", // center origin
                                // scrolling
                                transition:
                                  "opacity 300ms ease, top 100ms linear",
                                right: "-44px"
                              }}
                              onClick={() => handleButtonClick(cNum, "right")}
                            ></Button>
                          )}
                        </DtvBox>
                      </DtvBox>
                    </DtvBox>
                  ))}
              </DtvBox>
              {props?.ctas && (
                <DtvBox
                  sx={{
                    display: {
                      xs: "block",
                      sm: "flex",
                      md: "flex",
                      lg: "flex"
                    },
                    justifyContent: "center",
                    marginTop: "24px"
                  }}
                >
                  {props?.ctas?.map((cta, index) => {
                    // Sanitize once and feed both the href and the analytics attr, so a blocked
                    // scheme never reaches the DOM/GA payload via the unsanitized raw targeturl.
                    const safeUrl = safeHttpUrl(cta?.targeturl);
                    return (
                    <DtvButton
                      googleAnalyticsData={getAnalyticsAttrs(cta?.label?.toLowerCase(), 'modal_show', safeUrl)}
                      href={safeUrl}
                      id={`${id}_${cta?.id}`}
                      key={index}
                      label={cta?.label}
                      mode={cta?.mode}
                      promo={cta?.promo}
                      variant={cta?.variant}
                      sx={{
                        color: "#FFFFFF",
                        flexDirection: "row",
                        flexWrap: "wrap",
                        justifyContent: "center",
                        alignItems: "center",
                        verticalAlign: "baseline",
                        textAlign: "center",
                        marginLeft: {
                          sm: index > 0 ? "32px" : 0
                        },
                        marginTop: {
                          xs: index > 0 ? "20px" : 0,
                          sm: 0
                        }
                      }}
                    />
                  );
                  })}
                </DtvBox>
              )}
            </DtvBox>
          </EqualHeight>
        ) : (
          <DtvBox
            id={id}
            {...props.getSchemOrgData(true, SchemaTypes.MOVIE_CAROUSEL, "")}
            sx={{
              display: "flex",
              overflow: "hidden",
              flexFlow: "column nowrap",
              alignItems: "center",
              background: backgroundColor,
              padding: {
                xs: "32px 20px",
                lg: "68px 20px 53px 20px"
              },
              margin: {
                xs: "0 20px",
                sm: "0 32px",
                md: "0 100px",
                lg: "0 140px",
                xl: "0 180px"
              },
              transition: "padding 200ms ease margin 200ms ease",
              borderRadius: "10px",
              flexGrow: 1,
              ...props?.carousalStyles
            }}
          >
            {singleCarouselObj.content.headerImage?.url && (
              <DtvBox
                sx={{
                  display: "flex",
                  width: "100%",
                  height: "200px",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 0 10px 0"
                }}
              >
                <img
                  height="180px"
                  src={singleCarouselObj.content.headerImage?.url}
                  alt={
                    singleCarouselObj.content.headerImage?.alt ||
                    (singleCarouselObj.content.headerImage?.content ?
                      singleCarouselObj.content.headerImage?.content :
                      "Carousel header image")
                  }
                ></img>
              </DtvBox>
            )}
            {singleCarouselObj.content.header?.content && (
              <DtvBox
                sx={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "right",
                  px: {
                    xs: tileSpacing,
                    md: `calc(56px + ${tileSpacing})`
                  }
                }}
              >
                <DtvText
                  color={singleCarouselObj.content.header?.color}
                  content={singleCarouselObj.content.header?.content}
                  variant={singleCarouselObj.content.header?.variant || "body1"}
                  component={
                    singleCarouselObj.content.header?.component || "h3"
                  }
                  cta={{
                    ...(singleCarouselObj.content.header?.cta?.length > 0 &&
                      singleCarouselObj.content.header?.cta?.label?.trim() !==
                        "" && {
                        label:
                          singleCarouselObj.content.header?.cta?.label || "",
                        legalUrl:
                          singleCarouselObj.content.header?.cta?.legalUrl,
                        position:
                          singleCarouselObj.content.header?.cta?.position
                      })
                  }}
                  sx={{
                    color: singleCarouselObj.content.header?.color,
                    marginBottom: "24px",
                    textAlign: "center",
                    width: "100%"
                  }}
                  tooltip={{
                    ...(singleCarouselObj.content.header?.tooltip?.length > 0 &&
                      singleCarouselObj.content.header?.tooltip?.content?.trim() !==
                        "" && {
                        content:
                          singleCarouselObj.content.header?.tooltip?.content,
                        position:
                          singleCarouselObj.content.header?.tooltip?.position,
                        mode: singleCarouselObj.content.header?.tooltip?.mode,
                        sx: {
                          color: singleCarouselObj.content.header?.color
                        }
                      })
                  }}
                />
              </DtvBox>
            )}
            {(singleCarouselObj.content.subheading?.content ||
              isLiveScoreVariant) && (
              <DtvBox
                sx={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "right",
                  px: {
                    xs: tileSpacing,
                    md: `calc(56px + ${tileSpacing})`
                  },
                  ...(isLiveScoreVariant && {
                    flexDirection: {
 xs: "column",
sm: "row"
},
                    justifyContent: {
 xs: "start",
sm: "space-between"
},
                    alignItems: {
 xs: "flex-start",
sm: "center"
},
                    mb: "16px"
                  })
                }}
              >
                <DtvText
                  color={singleCarouselObj.content.subheading?.color}
                  content={singleCarouselObj.content.subheading?.content}
                  component={
                    singleCarouselObj.content.subheading?.component || "h3"
                  }
                  cta={{
                    ...(singleCarouselObj.content.subheading?.cta && {
                      label:
                        singleCarouselObj.content.subheading?.cta?.label || "",
                      legalUrl:
                        singleCarouselObj.content.subheading?.cta?.legalUrl,
                      position:
                        singleCarouselObj.content.subheading?.cta?.position
                    })
                  }}
                  sx={{
                    color: singleCarouselObj.content.subheading?.color,
                    marginBottom: "24px",
                    textAlign: "left",
                    width: "100%",
                    ml: "inherit",
                    ...(isLiveScoreVariant && {
                      m: "2px",
                      width: {
 xs: "100%",
sm: "60%"
}
                    })
                  }}
                  tooltip={{
                    ...(singleCarouselObj.content.subheading?.tooltip && {
                      content:
                        singleCarouselObj.content.subheading?.tooltip?.content,
                      position:
                        singleCarouselObj.content.subheading?.tooltip?.position,
                      mode: singleCarouselObj.content.subheading?.tooltip?.mode,
                      sx: {
                        color: singleCarouselObj.content.subheading?.color
                      }
                    })
                  }}
                  variant={
                    singleCarouselObj.content.subheading?.variant || "subhead2"
                  }
                />
                {isLiveScoreVariant && (
                  <DtvBox
                    aria-label={singleCarouselObj?.content?.showScoreLabel}
                    tabIndex={0}
                    sx={{
                      "& label": {
                        flexDirection: "row-reverse",
                        m: 0,
                        gap: { xs: "8px" }
                      }
                    }}
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        handleToggleScores();
                      }
                    }}
                    {...getAnalyticsAttrs(
                      `${showScores ? "hide" : "show"} scores`,
                      singleCarouselObj?.content?.header?.content || "",
                      href
                    )}
                  >
                    <DtvSwitch
                      checked={!!showScores}
                      label={singleCarouselObj?.content?.showScoreLabel}
                      onChange={handleToggleScores}
                    />
                  </DtvBox>
                )}
              </DtvBox>
            )}
            <DtvBox
              sx={{
                width: {
                  xs: "100%",
                  md: "calc(100% - 112px)"
                },
                transition: "width 300ms ease",
                flexGrow: 1,
                justifyContent: "center",
                position: "relative" // context for absolute position buttons
              }}
            >
              <DtvBox
                sx={{
                  display: "flex",
                  width: "100%",
                  overflow: "hidden"
                }}
              >
                {!props?.hideArrows && (
                  <Button
                    aria-disabled={true}
                    aria-label="Click to scroll the carousel left"
                    buttontype="tertiary"
                    icon-only="true"
                    iconOnly={true}
                    id={`${props?.id}-chevron-left-0`}
                    size="medium"
                    googleanalyticsdata={getAnalyticsAttrs(
                      `carousel-1-left-arrow`,
                      "panel_seasonal favorites & more",
                      href
                    )}
                    icon={
                      // @ts-expect-error DDS Icon component has stricter prop typing than actual usage
                      <LeftIcon />
                    }
                    mode={
                      backgroundColor.includes("FEFEFE") ||
                      backgroundColor.includes("FFF") ?
                        "light" :
                        "dark"
                    }
                    sx={{
                      // sizing
                      minWidth: "unset !important",
                      width: "48px !important",
                      height: "48px !important",
                      padding: "8px !important",
                      margin: "0 !important",
                      visibility: isDesktopUp ? "visible" : "hidden",
                      // position
                      position: "absolute",
                      transition: "opacity 300ms ease, top 100ms linear",
                      transform: "translateY(-50%)", // center origin
                      // scrolling
                      left: "-56px",
                      top:
                        cardImgHeight !== 0 ?
                          `${cardImgHeight / 2}px` :
                          !props?.isLoading ?
                          `calc(50% - ${estCardDescHeight / 2}px)` :
                          "50%",
                      opacity: "0.4"
                    }}
                    onClick={() => handleButtonClick(0, "left")}
                  ></Button>
                )}
                <DtvGrid
                  container
                  id={`${id}-card-container-0`}
                  sx={{
                    display: "flex",
                    flexWrap: "nowrap",
                    position: "relative",
                    justifyContent: props?.isLoading ? "flex-start" : "normal",
                    width: "100%",
                    overflowX: "auto",
                    scrollSnapType: "x proximity",
                    scrollbarWidth: "none",
                    scrollBehavior: "smooth",
                    paddingBottom: "10px",
                    "&::-webkit-scrollbar": {
                      width: "0px",
                      background: "transparent",
                      display: "none"
                    },
                    "&::-webkit-scrollbar-thumb": {
                      width: "0px",
                      background: "transparent",
                      display: "none"
                    }
                  }}
                  onScroll={() => handleScroll(0)}
                >
                  {props?.isLoading && singleCarouselObj?.mockCards}
                  {!props?.isLoading &&
                    props?.cards?.[0]?.resources &&
                    // eslint-disable-next-line complexity
                    props?.cards?.[0]?.resources?.map((card, index) => {
                      const startTime = channelData?.[card?.resourceId]?.consumables?.[0]?.startTime !== "NA" && new Date(channelData?.[card?.resourceId]?.consumables?.[0]?.startTime)
                      const endTime = channelData?.[card?.resourceId]?.consumables?.[0]?.endTime !== "NA" && new Date(channelData?.[card?.resourceId]?.consumables?.[0]?.endTime)
                      const endTimeFormatted = endTime.toLocaleTimeString([], {
 hour: 'numeric',
minute: '2-digit'
}).replace(/ AM| PM/, match => match.toLowerCase().charAt(1))
                      const startTimeFormatted = startTime.toLocaleTimeString([], {
 hour: 'numeric',
minute: '2-digit'
}).replace(/ AM| PM/, match => (endTimeFormatted[endTimeFormatted.length - 1] === match.toLowerCase().charAt(1) ? '' : match.toLowerCase().charAt(1)))
                      // Button semantics, keyboard activation and analytics all have to follow
                      // whether this card actually does something when it is activated.
                      const interactive =
                        !!card?.resourceId &&
                        !props?.disableCardClick &&
                        !isLiveScoreVariant;
                      return (
                      <DtvGrid
                        id={`card-resource-container-0-${index}`}
                        key={index}
                        role={interactive ? "button" : undefined}
                        tabIndex={interactive ? 0 : -1}
                        {...(interactive && {
                          onKeyDown: e => {
                            // A held Enter repeats the keydown; one activation is one event.
                            if (e.key === "Enter" && !e.repeat) {
                              reportCardKeyboardClick(card);
                              handleCardClick(card);
                            }
                          }
                        })}
                        {...handleInjectAnalytics(card, interactive)}
                        item
                        {...props.getSchemOrgData(
                          true,
                          SchemaTypes.LIST_ITEM,
                          SchemaProps.ITEM_LIST_ELEMENT
                        )}
                        sx={{
                          scrollSnapAlign: "start",
                          ...(!props?.mediaStyles?.cardStyles &&
                            (orientation === "v" ?
                              {
                                  "@media (min-width: 0px )": {
                                    maxWidth: "40%",
                                    minWidth: "40%"
                                  },
                                  "@media (min-width: 429px )": {
                                    maxWidth: "33%",
                                    minWidth: "33%"
                                  },
                                  "@media (min-width: 539px)": {
                                    maxWidth: "25%",
                                    minWidth: "25%"
                                  },
                                  "@media (min-width: 749px)": {
                                    maxWidth: "18.18%", // tablet 5 1/2
                                    minWidth: "18.18%"
                                  },
                                  "@media screen and (min-width: 999px)": {
                                    maxWidth: "14.28%",
                                    minWidth: "14.28%"
                                  },
                                  "@media screen and (min-width: 1699px)": {
                                    maxWidth: "12.5%",
                                    minWidth: "12.5%"
                                  }
                                } :
                              {
                                  "@media (min-width: 0px )": {
                                    maxWidth: "66%",
                                    minWidth: "66%"
                                  },
                                  "@media (min-width: 429px )": {
                                    maxWidth: "60%",
                                    minWidth: "60%"
                                  },
                                  "@media (min-width: 749px)": {
                                    maxWidth: "40%",
                                    minWidth: "40%"
                                  },
                                  "@media screen and (min-width: 1099px)": {
                                    maxWidth: "25%",
                                    minWidth: "25%"
                                  },
                                  "@media screen and (min-width: 1699px)": {
                                    maxWidth: "20%",
                                    minWidth: "20%"
                                  }
                                })),
                          ...props?.mediaStyles?.gridStyles
                        }}
                      >
                        <meta
                          content={String(index + 1)}
                          itemProp={SchemaProps.POSITION}
                        />
                        {card?.resourceId ? (
                          <Card
                            className={`${id}-card`}
                            key={index}
                            onClick={() => !isLiveScoreVariant && handleCardClick(card)}
                            {...handleInjectAnalytics(card, interactive)}
                            {...props.getSchemOrgData(
                              true,
                              SchemaTypes.MOVIE,
                              SchemaProps.ITEM
                            )}
                            sx={{
                              mx: {
 xs: "5px",
sm: tileSpacing
},
                              cursor:
                                card?.resourceId && !props?.disableCardClick ?
                                  isLiveScoreVariant ? "default" : "pointer" :
                                  "auto",
                              borderRadius: 0,
                              boxShadow: "none",
                              overflow: "initial",
                              background: "transparent",
                              ...props?.mediaStyles?.cardStyles,
                              ...(props?.mediaStyles?.cardStyles &&
                                index === 0 && {
                                  marginLeft: `0px !important`
                                })
                            }}
                          >
                            <DtvBox
                              sx={{
                                ...(isLiveScoreVariant && {
                                  position: "relative"
                                })
                              }}
                            >
                              {/* <DtvBox sx={{}}> */}
                              <CardMedia
                                className={`carousel_tile_${  id}`}
                                component={"img"}
                                id={`carousel_tile_${id}_${index}`}
                                itemProp={SchemaProps.IMAGE}
                                loading={"lazy"}
                                alt={
                                  card?.title ?
                                    card.title :
                                    card?.alt || "carousel card"
                                }
                                src={
                                  card.image?.imageUrl ?
                                    card.image?.imageUrl :
                                    "https://www.directv.com/dtvassets/global/component-assets/guide/EmptySVG.svg"
                                }
                                sx={{
                                  // height: card?.image?.height ? card?.image?.height : orientation === 'v' ? "232px" : "156px",
                                  width: card && card?.image?.width,
                                  aspectRatio:
                                    orientation !== "v" ? "1/0.56" : "1/1.4935",
                                  backgroundColor: card.bgColor,
                                  "&:hover": {
                                    boxShadow: isLiveScoreVariant ? "" : "0px 0px 5px #666",
                                    cursor: isLiveScoreVariant ? "default" : "pointer"
                                  },
                                  ...(props?.mediaStyles?.cardmediaStyles ||
                                    props?.cardmediaStyles)
                                }}
                                onError={e => {
                                  const target = e.target as HTMLImageElement;
                                  target.onerror = null;
                                  target.src = card.image?.defaultImageUrl;
                                }}
                              />
                              {isLiveScoreVariant &&
                                showScores &&
                                +card?.homeScore >= 0 &&
                                +card?.awayScore >= 0 && (
                                  <>
                                    <DtvText
                                      content={String(card?.awayScore)}
                                      variant={"body2medium"}
                                      sx={{
                                        ...styles.scoreDisplayStyles,
                                        left: {
                                          xs: "13%",
                                          sm: "17%",
                                          md: "14%",
                                          lg: "16%"
                                        }
                                      }}
                                    />
                                    <DtvText
                                      content={String(card?.homeScore)}
                                      variant={"body2medium"}
                                      sx={{
                                        ...styles.scoreDisplayStyles,
                                        right: {
                                          xs: "13%",
                                          sm: "17%",
                                          md: "14%",
                                          lg: "16%"
                                        }
                                      }}
                                    />
                                  </>
                                )}
                              {isLiveScoreVariant && showScores && (
                                <DtvBox
                                  sx={{
                                    position: "absolute",
                                    bottom: "5px",
                                    left:
                                    card?.gameStatus === "IN_PROGRESS" ?
                                        "7px" :
                                        "9px",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center"
                                  }}
                                >
                                  {card?.gameStatus === "IN_PROGRESS" && (
                                    <InProgressIcon />
                                  )}
                                  <DtvText
                                    content={
                                      card?.gameStatus === "IN_PROGRESS" ?
                                        card?.gamePeriod :
                                        card?.gameStatus === "COMPLETED" ?
                                        "Complete" :
                                        `Airing&nbsp;&nbsp;|&nbsp;&nbsp;${startTime.toString() === "Invalid Date" ? "" : (isToday(startTime) ? "Today," : isTomorrow(startTime) ? "Tomorrow," :  startTime.toLocaleDateString('en-US', {
 month: 'short',
day: 'numeric'
}))} ${startTime.toString() === "Invalid Date" ? "" : startTime.toLocaleTimeString([], {
 hour: 'numeric',
minute: '2-digit'
})}`
                                    }
                                    sx={{
                                      marginLeft:
                                      card?.gameStatus === "IN_PROGRESS" ?
                                          "4px" :
                                          "0px",
                                      color: "#FFFFFF",
                                      fontFamily: "roboto-regular",
                                      fontSize: "10px !important",
                                      fontStyle: "normal",
                                      fontWeight: 500,
                                      lineHeight: "130%"
                                    }}
                                  />
                                </DtvBox>
                              )}
                            </DtvBox>
                            {card?.title?.length > 0 && (
                              <CardContent
                                itemProp={SchemaProps.NAME}
                                sx={{
                                  padding: "0 !important",
                                  mt: "8px",
                                  height: "auto"
                                }}
                              >
                                <DtvText
                                  aria-label={card.title}
                                  component="div"
                                  content={card.title || ""}
                                  variant={"body2"}
                                  sx={
                                    showFromTitleKey ?
                                      { ...titleStyles.titleWrap } :
                                      { ...titleStyles.titleNoWrap },
                                      isLiveScoreVariant && {
color: "#000",
                                        fontSize: "14px",
                                        fontWeight: 400
                                      }
                                  }
                                />
                              </CardContent>
                            )}
                            {isLiveScoreVariant && <DtvBox sx={{
 display: "flex",
flexDirection: "column"
}}>
                              {channelData?.[card?.resourceId]?.genres && channelData?.[card?.resourceId]?.genres?.length > 0 && (
                                <DtvText
                                  content={`${channelData?.[card?.resourceId]?.genres?.[0]}&nbsp;&nbsp;|&nbsp;&nbsp;${startTimeFormatted === "Invalid Date" ? "" : startTimeFormatted} - ${endTimeFormatted === "Invalid Date" ? "" : endTimeFormatted}`}
                                  variant="label"
                                  sx={{
 fontSize: "12px",
color: "#8D8D8D"
}}
                                />
                              )}
                              {channelData?.[card?.resourceId]?.consumables?.[0]?.channel?.majorChannelNumber && (
                                <DtvText
                                  content={`Channel ${channelData?.[card?.resourceId]?.consumables?.[0]?.channel?.majorChannelNumber}`}
                                  variant="label"
                                  sx={{
 fontSize: "12px",
color: "#8D8D8D"
}}
                                />
                              )}
                              </DtvBox>}
                            {card?.description?.length > 0 && (
                              <CardContent itemProp={SchemaProps.DESCRIPTION} sx={{
 padding: '0 !important',
height: '19px'
}}>
                                <DtvText
                                  aria-label={card.description || ""}
                                  component="div"
                                  content={card.description || ""}
                                  variant="smalltext"
                                  sx={{
                                    whiteSpace: "nowrap",
                                    lineHeight: "normal",
                                    pb: "3px",
                                    textOverflow: "ellipsis",
                                    color: props?.color,
                                    cursor: "pointer",
                                    overflow: "hidden",
                                    marginTop: "4px"
                                  }}
                                />
                              </CardContent>
                            )}
                            <meta
                              itemProp={SchemaProps.URL}
                              content={`/guide/movies/${card?.title
                                ?.replace?.(/[^a-zA-Z0-9 ]/g, "")
                                ?.replace?.(/\s+/g, "-")}-${card.resourceId}`}
                            />
                            {card.mediaModalData?.subheading &&
                              card.mediaModalData.subheading.includes("|") && (
                                <meta
                                  itemProp={SchemaProps.GENRE}
                                  content={card.mediaModalData.subheading
                                    .split("|")
                                    .pop()
                                    .trim()}
                                />
                              )}
                          </Card>
                        ) : (
                          renderMockCard("double", index)
                        )}
                      </DtvGrid>
                    )
})}
                </DtvGrid>
                {!props?.hideArrows && (
                  <Button
                    aria-disabled={false}
                    aria-label="Click to scroll the carousel right"
                    buttontype="tertiary"
                    icon-only="true"
                    iconOnly={true}
                    id={`${props?.id}-chevron-right-0`}
                    size="medium"
                    googleanalyticsdata={getAnalyticsAttrs(
                      `carousel-1-right-arrow`,
                      "panel_seasonal favorites & more",
                      href
                    )}
                    icon={
                      // @ts-expect-error DDS Icon component has stricter prop typing than actual usage
                      <RightIcon
                        mode={props?.mode === "dark" ? "dark" : "light"}
                      />
                    }
                    mode={
                      backgroundColor.includes("FEFEFE") ||
                      backgroundColor.includes("FFF") ?
                        "light" :
                        "dark"
                    }
                    sx={{
                      // sizing
                      minWidth: "unset !important",
                      width: "48px !important",
                      height: "48px !important",
                      padding: "8px !important",
                      margin: "0 !important",
                      visibility: isDesktopUp ? "visible" : "hidden",
                      // position
                      position: "absolute",
                      top:
                        cardImgHeight !== 0 ?
                          `${cardImgHeight / 2}px` :
                          !props?.isLoading ?
                          `calc(50% - ${estCardDescHeight / 2}px)` :
                          "50%",
                      transform: "translateY(-50%)", // center origin
                      // scrolling
                      transition: "opacity 300ms ease, top 100ms linear",
                      right: "-56px"
                    }}
                    onClick={() => handleButtonClick(0, "right")}
                  ></Button>
                )}
              </DtvBox>
            </DtvBox>

            {props?.ctas?.length > 0 && (
              <DtvBox
                sx={{
                  display: {
                    xs: "block",
                    sm: "flex"
                  },
                  justifyContent: "center",
                  marginTop: "24px"
                }}
              >
                {props?.ctas?.map((cta, index) => {
                  // Sanitize once and feed both the href and the analytics attr, so a blocked
                  // scheme never reaches the DOM/GA payload via the unsanitized raw targeturl.
                  const safeUrl = safeHttpUrl(cta?.targeturl);
                  return (
                  <DtvButton
                    googleAnalyticsData={getAnalyticsAttrs(cta?.label?.toLowerCase(), 'modal_show', safeUrl)}
                    href={safeUrl}
                    id={`${id}_${cta?.id}`}
                    key={index}
                    label={cta?.label}
                    mode={cta?.mode}
                    promo={cta?.promo}
                    variant={cta?.variant}
                    sx={{
                      flexDirection: "row",
                      flexWrap: "wrap",
                      justifyContent: "center",
                      alignItems: "center",
                      verticalAlign: "baseline",
                      textAlign: "center",
                      marginLeft: {
                        sm: index > 0 ? "32px" : 0
                      },
                      marginTop: {
                        xs: index > 0 ? "20px" : 0,
                        sm: 0
                      }
                    }}
                  />
                  );
                })}
              </DtvBox>
            )}
          </DtvBox>
        )}
      </>
    );
  } catch (error) {
    return <ErrorDisplay error={error} />;
  }
}

export function ShowCarousal(props) {
  const showMockContentOnApiFailure = props?.showMockContentOnApiFailure;
  const [carouselResources, setCarouselResources] = useState<any[]>();
  // Stable card array for the parallax child so its React.memo cards aren't invalidated by a new
  // array on every ShowCarousal render.
  const parallaxCards = useMemo(
    () => (carouselResources || []).flatMap(carousel => carousel?.resources || []),
    [carouselResources]
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [headerImages, setHeaderImages] = useState();
  const [headers, setHeaders] = useState();
  const [subheadings, setSubheadings] = useState();
  const [ctas, setCtas] = useState<any[]>([]);
  const isLiveScoreVariant = !!props?.liveScoreConfig;
  const mockResources = getMockResources(props?.backgroundColor);
  // Requested image specs and the slot the modal hero lands on in each resource's `images[]`.
  // Memoized on the props it reads, so the array is stable across the loading, resource, channel
  // and header re-renders this component goes through. Destructured first so the dependency list
  // is plain values.
  const {
    variant: fisVariant,
    orientation: fisOrientation,
    horizontalProps: fisHorizontalProps,
    mediaModal: fisMediaModal
  } = props || {};
  const { specs: fisSpecs, heroIndex } = useMemo(
    () => buildFisSpecs(props),
    [fisVariant, fisOrientation, fisHorizontalProps, fisMediaModal]
  );
  const disableSchemaOrg = props?.disableSchemaOrg || false;
  const getSchemOrgData = (itemScope, itemType, itemProp) => (!disableSchemaOrg ? {
    itemScope,
    itemType,
    itemProp
  } : {});
  const carouselConfig = props?.carouselId ?
    [
      {
        id: props?.carouselId,
        count:
            props?.orientation === "h" ?
              props?.itemCount && props?.itemCount >= 5 ?
                props?.itemCount :
                5 :
              props?.itemCount && props?.itemCount >= 8 ?
                props?.itemCount :
                8
      }
    ] :
    props?.carousels ?
      Array.isArray(props?.carousels) &&
      props?.carousels?.length > 0 &&
      props?.carousels?.[0]?.carouselId &&
      props?.carousels?.[1]?.carouselId ?
        props?.carousels?.map(carousel => ({
          id: carousel.carouselId,
          count:
              props?.orientation === "h" ?
                carousel.itemCount && carousel.itemCount >= 5 ?
                  carousel.itemCount :
                  5 :
                carousel.itemCount && carousel.itemCount >= 8 ?
                  carousel.itemCount :
                  8
        })) :
        Array.isArray(props?.carousels) && props?.carousels?.[0]?.carouselId ?
          [
            {
              id: props?.carousels?.[0]?.carouselId,
              count:
              props?.orientation === "h" ?
                props?.carousels?.[0]?.itemCount &&
                  props?.carousels?.[0]?.itemCount >= 5 ?
                  props?.carousels?.[0]?.itemCount :
                  5 :
                props?.carousels?.[0]?.itemCount &&
                  props?.carousels?.[0]?.itemCount >= 8 ?
                  props?.carousels?.[0]?.itemCount :
                  8
            }
          ] :
          props?.carousels?.carouselId ?
            [
              {
                id: props?.carousels?.carouselId,
                count:
              props?.orientation === "h" ?
                props?.carousels?.itemCount &&
                  props?.carousels?.itemCount >= 5 ?
                  props?.carousels?.itemCount :
                  5 :
                props?.carousels?.itemCount &&
                  props?.carousels?.itemCount >= 8 ?
                  props?.carousels?.itemCount :
                  8
              }
            ] :
            [] :
      [];
  const [channelData, setChannelData] = useState({});
  // Series-level network name and artwork keyed by seriesId. Held in a side-car map instead of
  // being merged into carouselResources: replacing that array re-runs every effect keyed on it
  // and invalidates the parallaxCards memo, whose whole purpose is keeping the child's memoized
  // cards stable.
  const [showEnrichment, setShowEnrichment] = useState({});
  // Series ids already requested, so a later pass — a live-score refresh replacing the cards
  // array — never re-requests one, including the ones that failed.
  const requestedSeries = useIdSet();
  // Unmount guard only. Cancelling per effect run instead would drop a batch already on the wire,
  // because cards can arrive in more than one pass.
  const isMountedRef = useRef(true);

  useEffect(() => {
    // The parallax variant renders show posters only — it never surfaces channel numbers,
    // so skip the extra channel-lineup auth/media fetch that getChannelNumbers triggers.
    if (props?.variant === "parallax") {
      return;
    }
    getChannelNumbers();
  }, [carouselResources]);

  // Set on mount as well as cleared on unmount: a remount (StrictMode's double effect invoke)
  // reuses the same ref, and leaving it false would silently discard every later result.
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Deliberately the inverse of the channel-number skip above: that fetch is skipped for the
  // parallax variant, this one runs only for it. The refreshed tile is the only layout with a
  // network line and series artwork, and the classic variant must keep firing exactly the
  // requests it fires today.
  useEffect(() => {
    if (props?.variant !== "parallax") {
      return;
    }
    // Episodes of one series share a seriesId, so de-duping collapses a 25-tile carousel to one
    // request per distinct series.
    const pending = Array.from(
      new Set(
        parallaxCards
          .filter(card => card?.contentType === "EPISODE" && card?.seriesId)
          .map(card => card.seriesId)
      )
    ).filter(seriesId => !requestedSeries.has(seriesId));
    if (!pending.length) {
      return;
    }
    // Marked before the fetch, not after, so a re-render while a wave is on the wire cannot open a
    // second request for the same id.
    pending.forEach(seriesId => requestedSeries.add(seriesId));
    // Nothing awaits this, and a throw outside the per-request loop below (a missing
    // Promise.allSettled, say) never reaches that loop's own warn — so without this the wave fails
    // as an unhandled rejection carrying no diagnostic at all.
    fetchShowEnrichment(pending, 0).catch(error => {
      console.warn("[ShowCarousal] series enrichment dispatch failed", error);
    });
  }, [parallaxCards, props?.variant]);

  useEffect(() => {
    setHeaderImages(
      props?.carousels && Array.isArray(props?.carousels) ?
        props?.carousels.map(carousel => (Array.isArray(carousel.headerImage) ?
          carousel?.headerImage?.[0] :
          carousel?.headerImage)) :
        props?.carousels?.headerImage ?
          Array.isArray(props?.carousels?.headerImage) ?
            props?.carousels?.headerImage?.[0] :
            props?.carousels?.headerImage :
          props?.headerImage ?
            Array.isArray(props?.headerImage) ?
              props?.headerImage?.[0] :
              props?.headerImage :
            []
    );
    setHeaders(
      props?.carousels && Array.isArray(props?.carousels) ?
        props?.carousels.map(carousel => (Array.isArray(carousel.header) ?
          carousel?.header?.[0] :
          carousel?.header)) :
        props?.carousels?.header ?
          Array.isArray(props?.carousels?.header) ?
            props?.carousels?.header?.[0] :
            props?.carousels?.header :
          props?.header ?
            Array.isArray(props?.header) ?
              props?.header?.[0] :
              props?.header :
            []
    );
    setSubheadings(
      props?.carousels && Array.isArray(props?.carousels) ?
        props?.carousels.map(carousel => (Array.isArray(carousel.subheading) ?
          carousel?.subheading?.[0] :
          carousel?.subheading)) :
        props?.carousels?.subheading ?
          Array.isArray(props?.carousels?.subheading) ?
            props?.carousels?.subheading?.[0] :
            props?.carousels?.subheading :
          props?.subHeading ?
            Array.isArray(props?.subHeading) ?
              props?.subHeading?.[0] :
              props?.subHeading :
            []
    );
    setCtas(
      props?.ctas ?
        !Array.isArray(props?.ctas) ?
          [props.ctas] :
          props?.ctas :
        []
    );
    carouselAPI();
  }, []);

  // Fetch live scores every 60 seconds and update only if changed
  useEffect(() => {
    if (!isLiveScoreVariant) {
      return;
    }
    fetchAndUpdateScores(props?.liveScoreConfig?.apiUrl); // Show scores on initial mount
    const intervalId = setInterval(
      () => fetchAndUpdateScores(props?.liveScoreConfig?.apiUrl),
      props?.liveScoreConfig?.refreshInterval || 60 * 1000
    );
    return () => clearInterval(intervalId);
  }, [isLiveScoreVariant]);

  // Fallback for a carousel fetch that produced nothing. `showMockContentOnApiFailure === false`
  // renders no cards at all, which is the existing contract when CI disables mock content.
  const applyMockResources = mockContent => {
    if (showMockContentOnApiFailure === false) {
      return;
    }
    setCarouselResources(
      (Array.isArray(props?.carousels) && props?.carousels?.length > 1) ||
        carouselConfig?.length > 1 ?
        [{ resources: mockContent }, { resources: mockContent }] :
        [{ resources: mockContent }]
    );
  };

  const carouselAPI = async () => {
    const browserId =
      props?.productType === "stream" ?
        "DFW_WEB_Chrome_G" :
        "DTVE_DFW_WEB_Chrome_G";
    const mockContent =
      showMockContentOnApiFailure !== undefined &&
      showMockContentOnApiFailure === false ?
        {} :
        mockResources;
    if (props?.pageKey) {
      try {
        const itemCount = props?.itemCount && Number(props?.itemCount) >= 5
          ? Number(props?.itemCount)
          : 20;
        // These fetches encode the query themselves, so the spec separator stays a raw '#'.
        const fisPropertiesFull = `ISF:1.0#${fisSpecs.join('#')}`;
        const pageKeyNorm = props.pageKey.toLowerCase().trim();
        let cfg: GenreConfig;
        if (props.configType === 'genre') {
          cfg = { type: 'genre', genreType: props.genreType || '', showType: 'show' };
        } else if (props.configType === 'package') {
          cfg = {
            type: 'package',
            packageFlag: props.packageFlag || '',
            fallbackFlag: props.packageFallbackFlag,
            seedChannels: props.seedChannels ? props.seedChannels.split(',').map(s => s.trim()) : [],
            genresToSearch: props.genresToSearch ? props.genresToSearch.split(',').map(s => s.trim()) : [],
          };
        } else {
          cfg = {
            type: 'keyword',
            query: props.keywordQuery || '',
            facetFilter: props.keywordFacetFilter,
            filterTerms: props.keywordFilterTerms ? props.keywordFilterTerms.split(',').map(s => s.trim()) : undefined,
          };
        }
        if (!cfg.type) throw new Error(`[ShowCarousal] configType is required for pageKey: "${pageKeyNorm}"`);
        const tokenResponse = await getAuthNToken(browserId);
        const token = tokenResponse?.access_token;
        let resources: any[];
        if (cfg.type === 'genre') {
          resources = await _fetchByGenre(cfg, token, fisPropertiesFull, itemCount);
        } else if (cfg.type === 'package') {
          resources = await _fetchByPackage(cfg, token, fisPropertiesFull, itemCount);
        } else {
          resources = await _fetchByKeyword(cfg, token, fisPropertiesFull, itemCount);
        }
        const cleaned = cleanResources({ resources });
        setCarouselResources([{ resources: cleaned }]);
      } catch (err) {
        console.error('[ShowCarousal] pageKey fetch failed, falling back', err);
        if (showMockContentOnApiFailure !== false) {
          setCarouselResources([{ resources: mockContent }]);
        }
      } finally {
        setIsLoading(false);
      }
      return;
    }
    await getAuthNToken(browserId)
      .then(accessTokenResponse => {
        const access_token = accessTokenResponse?.access_token;
        return getOVResources(
          props?.referenceId,
          access_token,
          // getCarousel interpolates this into the URL as-is, so the separator must be pre-encoded
          // (a raw '#' would make everything after it a URL fragment).
          fisSpecs.join("%23"),
          mockContent
        );
      })
      .then((res: any) => {
        const carousels = (Array.isArray(res) ? res : []).map(cd => ({ resources: cd }));
        // getCarousel never rejects: a non-ok response resolves with a function and a thrown one
        // resolves undefined, so a refused request arrives here as a carousel with no resources.
        // Degrade per carousel — a failed leg must not discard a healthy sibling's real content.
        if (!carousels.length || carousels.every(carousel => !carousel.resources)) {
          applyMockResources(mockContent);
          return;
        }
        // A failed leg among healthy ones still honours the CI mock-content switch: with mock
        // content disabled it stays empty rather than filling with placeholder cards.
        const degraded = showMockContentOnApiFailure === false ? undefined : mockContent;
        setCarouselResources(
          carousels.map(carousel => (carousel.resources ?
            carousel :
            { resources: degraded }))
        );
        if (isLiveScoreVariant) {
          fetchAndUpdateScores(props?.liveScoreConfig?.apiUrl);
        }
      })
      .catch(() => applyMockResources(mockContent))
      .finally(() => setIsLoading(false));
  };

  const getNumber = async card => {
    const contentType =
      card?.resourceType.toLowerCase() === "series" ?
        "tv-shows" :
        card.resourceType.toLowerCase() === "network" ?
          "networks" :
          "movies";
    const browserId =
      props?.productType === "stream" ?
        "DFW_WEB_Chrome_G" :
        "DTVE_DFW_WEB_Chrome_G";

    const accessTokenResponse = await getAuthNToken(browserId);
    // eslint-disable-next-line no-return-await
    return await new Promise((resolve, _reject) => {
      const res: any = getmediaModal(
        contentType,
        card.resourceId,
        accessTokenResponse.access_token
      );
      resolve(res);
    });
  }

  const getChannelNumbers = () => {
    carouselResources?.[0]?.resources?.forEach(resource => {
      getNumber(resource).then((data: any) => {
        setChannelData(
          prevData => (
            {
              ...prevData,
              [resource.resourceId]: data || {}
            }
          )
        )
      }).catch(_error => "")
    });
  };

  // Fills in what an episode-derived card cannot carry: a carousel built from episodes has no
  // network name and no poster art on the resource itself, only the seriesId whose details hold
  // both. One wave of SHOW_ENRICHMENT_CHUNK requests at a time — recursive rather than looped so
  // the awaits stay out of a loop body — so a full carousel never opens a socket per tile. A
  // request that fails is skipped and its card keeps exactly the values the feed gave it.
  const fetchShowEnrichment = async (seriesIds: string[], start: number) => {
    if (start >= seriesIds.length || !isMountedRef.current) {
      return;
    }
    const chunk = seriesIds.slice(start, start + SHOW_ENRICHMENT_CHUNK);
    const settled = await Promise.allSettled(
      chunk.map(seriesId => fetchSeriesDetails(seriesId))
    );
    if (!isMountedRef.current) {
      return;
    }
    const resolved = {};
    settled.forEach((result, offset) => {
      if (result.status === "rejected") {
        console.warn(
          `[ShowCarousal] series-details enrichment failed for ${chunk[offset]}`,
          result.reason
        );
        return;
      }
      if (result.value) {
        resolved[chunk[offset]] = result.value;
      }
    });
    if (Object.keys(resolved).length) {
      setShowEnrichment(prev => ({
        ...prev,
        ...resolved
      }));
    }
    await fetchShowEnrichment(seriesIds, start + SHOW_ENRICHMENT_CHUNK);
  };

  const getOVResources = async (
    referenceId,
    access_token,
    fisProperties,
    mockContent
  ) => {
    const config = carouselConfig;
    const type = config?.length > 1 ? 2 : config?.length > 0 ? 1 : 0;
    if (type === 2) {
      // eslint-disable-next-line no-return-await
      return await Promise.all([
        getCarousel(
          "generic",
          config[0].id,
          referenceId,
          access_token,
          "",
          fisProperties,
          config[0].count
        )
          .catch(() => mockContent)
          .then(
            res => cleanResources(res),
            errRes => [{ resources: errRes }]
          ),
        getCarousel(
          "generic",
          config[1].id,
          referenceId,
          access_token,
          "",
          fisProperties,
          config[1].count
        )
          .catch(() => mockContent)
          .then(
            res => cleanResources(res),
            errRes => [{ resources: errRes }]
          )
      ]);
    } else if (type === 1) {
      // eslint-disable-next-line no-return-await
      return await Promise.all([
        getCarousel(
          "generic",
          config[0].id,
          referenceId,
          access_token,
          "",
          fisProperties,
          config[0].count
        )
          .catch(() => mockContent)
          .then(
            res => cleanResources(res),
            errRes => errRes
          )
      ]);
    }
    throw new Error("Carousel not configured properly.");
  };

  // Resolve the modal CTAs for a given show. A per-show override (keyed by resourceId in CMS)
  // wins over the carousel-level mediaModalCta1/mediaModalCta2 defaults.
  const resolveShowCta = (resourceId: any) => {
    const override = props?.ctaOverrides?.[resourceId];
    return {
      cta1: override?.cta1 ?? props?.mediaModalCta1,
      cta2: override?.cta2 ?? props?.mediaModalCta2
    };
  };

  // Returns undefined for a carousel the feed did not deliver (see applyMockResources) rather than
  // throwing, so a refused request degrades to mock content instead of an unhandled TypeError.
  // eslint-disable-next-line complexity
  const cleanResources = carousel => carousel?.resources?.map((resource: any, ix) => {
    const contentType =
        resource?.resourceType?.toLowerCase() === "series" ?
          "tv-shows" :
          resource?.resourceType?.toLowerCase() === "network" ?
            "networks" :
            "movies";
    let subHead: any = "";
    if (
      (resource?.contentType === "EPISODE" ||
          resource?.contentType === "SPORT") &&
        resource?.title
    ) {
      subHead = resource?.releaseYear ?
          `${resource?.title || ""} (${resource?.releaseYear || ""})` :
        resource?.title || "";
      if (
        resource?.seasonNumber ||
          resource?.episodeNumber ||
          resource?.episodeTitle
      ) {
        const seasonDetails: any = `${
            resource?.seasonNumber ? `S${resource?.seasonNumber}` : ""
          } ${resource?.episodeNumber ? `E${resource?.episodeNumber}` : ""} ${
            resource?.seasonNumber || resource?.episodeNumber ? "|" : ""
          } ${
            resource?.episodeTitle ?
              `${
                  resource?.contentType === "SPORT" ?
                    resource?.releaseYear ?
                      `${resource?.title} <br> ${resource?.episodeTitle} (${resource?.releaseYear})` :
                      `${resource?.episodeTitle  } ${  resource?.title}` :
                    resource?.episodeTitle
                }` :
              ""
          }`;
        subHead = subHead && seasonDetails ? ` | ${seasonDetails}` : subHead;
      }
    } else if (resource?.title) {
      const releaseYear: any = resource?.releaseYear ?
          `${resource?.title} (${resource?.releaseYear})` :
        resource?.title;
      subHead = subHead && releaseYear ? ` | ${releaseYear}` : subHead;
    }
    // if (resource?.consumables?.length && resource?.consumables[0]?.startTime) {
    //   let startTime: any = resource?.consumables[0]?.startTime ? `Airing | ${dateToFormat(resource?.consumables?.[0]?.startTime)}` : '';
    //   subHead += subHead != '' && startTime ? ` | ${startTime}` : startTime;
    // }
    if (
      resource?.parentalRating ||
        resource?.genres ||
        resource?.consumables
    ) {
      const details: any = `${
          resource?.parentalRating ? `${resource?.parentalRating} |` : ""
        } ${
          resource?.genres?.length ?
            `${resource?.genres?.map((genre: any) => ` ${  genre}`)}` :
            ""
        } ${
          // resource?.consumables?.[0]?.provider?.friendlyName
          // ? `| ${resource?.consumables[0]?.provider?.friendlyName}`
          // : ""} ${
          resource?.networkAttributionName ?
            `| ${resource?.networkAttributionName}` :
            ""
        }`;
      subHead +=
          subHead && details ? ` | ${details.trim()}` : `${details.trim()}`;
    }
    const cardTitle =
        props?.showTitle?.toLowerCase?.() === "no" ?
          "" :
          props?.showFromTitleKey ?
            resource?.displayTitle :
            resource?.title || "";
    const mediaModalTitle: string = resource?.title ?
      resource?.title :
      resource?.displayTitle;
    const showCta = resolveShowCta(resource?.resourceId);
    // The tile shows the poster spec, which is the asset carrying the show's title treatment. Every
    // index is optional-chained: this runs before the `images?.length` guard on the returned object,
    // and the feed does omit `images` on some resources.
    const tileImage = !resource?.images?.[0]?.imageUrl ?
      resource?.images?.[1] :
      resource?.images?.[0];
    // The modal hero is whichever slot the hero spec was requested in; -1 means it was not asked for.
    const heroImage = heroIndex >= 0 ? resource?.images?.[heroIndex] : undefined;
    // Placeholder colour comes from the image that actually paints, falling back to the poster's own
    // colour when that image carries none. The feed delivers an "r,g,b" triplet, so it needs
    // wrapping; the mock fallback is already a hex string and must not be wrapped, or the
    // declaration is invalid CSS and the browser drops it.
    const tileColorTriplet =
        tileImage?.dominantColor?.background ||
        tileImage?.defaultDominantColor?.background ||
        resource?.images?.[0]?.dominantColor?.background ||
        resource?.images?.[0]?.defaultDominantColor?.background;
    // Index guard: CMS authors item counts past the end of the mock list, and throwing here would
    // reject the whole fetch and leave the carousel on placeholders for good.
    const tileBgColor = tileColorTriplet ?
      `rgb(${tileColorTriplet})` :
      mockResources[ix]?.bgColor || DEFAULT_TILE_BG_COLOR;

    return resource?.resourceId &&
        resource?.resourceType &&
        resource?.images?.length &&
        resource?.images?.[0] ?
      {
        resourceId: resource.resourceId,
        resourceType: contentType,
        // The feed's own content type, kept as-is: `resourceType` above is overwritten with the
        // URL slug, so it can no longer tell a consumer the card came from an episode. Paired with
        // `seriesId`, it is what the refreshed tile's series enrichment keys off.
        contentType: resource?.contentType,
        seriesId: resource?.seriesId,
        title: cardTitle,
        description:
              props?.showDescription?.toLowerCase?.() === "yes" ?
                resource?.description :
                "",
        image: tileImage,
        network: resolveNetworkName(resource),
        alt: props?.showFromTitleKey ?
          resource?.title :
          resource?.displayTitle || "",
        bgColor: tileBgColor,
        mediaModalData: {
          id: "mediaModal",
          contentType,
          resourceId: resource?.resourceId,
          dominantColor: heroImage?.dominantColor?.background,
          defaultImageUrl: heroImage?.defaultImageUrl,
          imageUrl: heroImage?.imageUrl,
          altImage:
                "https://www.directv.com/dtvassets/global/component-assets/guide/EmptySVG.svg",
          imgWidth: `${heroImage?.width || "0"}px`,
          imgHeight: `${heroImage?.height || "0"}px`,
          heading: mediaModalTitle,
          subheading: subHead,
          body: resource?.description || "",
          cta1: showCta.cta1,
          cta2: showCta.cta2,
          modalOpen: false,
          cropImage: props?.cropImage,
          enableChannelInfo: props?.enableChannelInfo
        }
      } :
      {
        ...mockResources[ix]
      };
  });

  const fetchAndUpdateScores = async (
    liveGamesUrl = "https://d3pvtvryiw501j.cloudfront.net/LiveScoreUpdate.json?clientContext=dmaID:0"
  ) => {
    try {
      const response = await fetch(liveGamesUrl);
      const data = await response.json();
      setCarouselResources(prev => {
        if (!prev || !prev[0] || !Array.isArray(prev[0].resources)) {
          return prev;
        }
        const updatedResources = prev[0].resources.map(resource => {
          const match = data?.liveGames?.find(game => game?.canonicalId === resource?.resourceId);
          if (match) {
            return {
              ...resource,
              awayScore: match?.awayScore,
              homeScore: match?.homeScore,
              gameStatus: match?.gameStatus,
              gamePeriod: match?.gamePeriod
            };
          }
          return {
            ...resource,
            awayScore: -1,
            homeScore: -1,
            gameStatus: "NA",
            gamePeriod: "NA"
          };
        });
        return [{
          ...prev[0],
          resources: updatedResources
        }];
      });
    } catch (err) {
      console.error("Live score fetch failed", err);
    }
  };

  // The parallax variant renders through a dedicated child. Kept in its own render
  // helper so its prop-passing branches count against this closure, not the ShowCarousal complexity.
  const renderParallaxVariant = () => (
    <ShowCarousalParallax
      backgroundColor={props?.backgroundColor}
      cards={parallaxCards}
      channelLabel={props?.channelLabel}
      color={props?.color}
      disableCardClick={props?.disableCardClick}
      enableChannelInfo={props?.enableChannelInfo}
      enrichment={showEnrichment}
      // headers/subheadings are set in a mount effect, so they are undefined during SSR.
      // Fall back to the raw props so the title/subheading exist in the server render
      // (toText normalises the array/object CMS shape) — otherwise the section paints
      // untitled and the heading pops in on hydration.
      header={headers ?? props?.header}
      id={props?.id || "dtv-show-carousal"}
      isLoading={isLoading}
      // Normalized here so the child's `=== false` / `!== false` guards agree with the hero image
      // spec this component requested; CMS can deliver the string form of either boolean.
      mediaModal={isMediaModalEnabled(props?.mediaModal)}
      productType={props?.productType}
      rows={props?.rows}
      subHeading={subheadings ?? props?.subHeading}
    />
  );

  if (props?.variant === "parallax") {
    return renderParallaxVariant();
  }

  return (
    <>
      <CarouselComponent
        backgroundColor={props?.backgroundColor}
        cardmediaStyles={props?.cardmediaStyles}
        cards={carouselResources}
        carousalStyles={props?.carousalStyles}
        channelData={channelData}
        channelLabel={props?.channelLabel}
        color={props?.color}
        ctas={ctas}
        disableCardClick={props?.disableCardClick}
        enableChannelInfo={props?.enableChannelInfo}
        getSchemOrgData={getSchemOrgData}
        headerImages={headerImages}
        headers={headers}
        hideArrows={props?.hideArrows}
        id={props?.id || "dtv-show-carousal"}
        isDblCarousel={props?.isDblCarousel}
        isLiveScoreVariant={props?.liveScoreConfig}
        isLoading={isLoading}
        liveScoreConfig={props?.liveScoreConfig}
        mediaModal={props?.mediaModal}
        mediaModalCta1={props?.mediaModalCta1}
        mediaModalCta2={props?.mediaModalCta2}
        mediaStyles={props?.mediaStyles}
        openCardClickInNewTab={props?.openCardClickInNewTab}
        orientation={props?.orientation}
        showDescription={props?.showDescription}
        showFromTitleKey={props?.showFromTitleKey}
        showTitle={props?.showTitle}
        subHeadings={subheadings}
      />
    </>
  );
}
